/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable max-lines */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useNavigationType, useParams } from "react-router-dom"
import { toast } from "sonner"

import type {
  ChatAppResponse,
  ChatSessionModel,
  GPTModelItem,
  PromptLibraryItem,
  WorkflowItem,
} from "@/api/models"
import type { DashboardElement, DashboardSpec } from "@/features/dash/dashboard/catalog"
import type { FlowRun } from "@/features/workflow-editor/hooks/use-workflow-runs"

import { Eye, PanelRightClose, PanelRightOpen, Play, Share2 } from "@/components/icons"
import { TextSelectionProvider } from "@/components/text-selection-menu/text-selection-provider"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { SidebarInset } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { AgentModeInfoModal } from "@/features/ai-explorer/components/agent-mode-info-modal"
import { ExplorerFooterToolbar } from "@/features/ai-explorer/components/explorer-footer-toolbar"
import { ImageReasoningWarningModal } from "@/features/ai-explorer/components/image-reasoning-warning-modal"
import { M365InfoModal } from "@/features/ai-explorer/components/m365-info-modal"
import { ResearchSettingsSidebar } from "@/features/ai-explorer/components/research-settings-sidebar"
import { VoiceModeInfoModal } from "@/features/ai-explorer/components/voice-mode-info-modal"
import { useProjectDefaults } from "@/features/ai-explorer/hooks/use-project-defaults"
import { useUncancellableFlowRunSessions } from "@/features/ai-explorer/hooks/use-uncancellable-flow-run-sessions"
import { BaseSettingsSidebar } from "@/features/base-settings/components/base-settings-sidebar"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { Chat } from "@/features/chat/components/chat"
import { ChatHome } from "@/features/chat/components/chat-home"
import { ChatShareDialog } from "@/features/chat/components/chat-share-dialog"
import { ReasoningModePill } from "@/features/chat/components/reasoning-mode-pill"
import { SharedHistoryMarkerLine } from "@/features/chat/components/shared-history-marker"
import { StartNewChat } from "@/features/chat/components/start-new-chat"
import { ToolApprovalOverlay } from "@/features/chat/components/tool-approval-overlay"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useSharedHistoryMarkers } from "@/features/chat/hooks/use-shared-history-markers"
import { useVoiceEngine } from "@/features/chat/hooks/use-voice-engine"
import { getSessionErrorCode } from "@/features/chat/utils/session-error-code"
import { hasVoiceInternalDataConsent } from "@/features/chat/utils/voice-mode-consent"
import { AiRefreshConfirmDialog } from "@/features/dash/components/ai-refresh-confirm-dialog"
import { DashboardBreadcrumb } from "@/features/dash/components/dashboard-breadcrumb"
import { DashboardLanding } from "@/features/dash/components/dashboard-landing"
import { DashboardSidebarContent } from "@/features/dash/components/dashboard-sidebar-content"
import { useDashChat } from "@/features/dash/hooks/use-dash-chat"
import {
  useCreateDashboard,
  useDashboards,
  useUpdateDashboard,
} from "@/features/dash/hooks/use-dashboards"
import { DashboardService, type SavedDashboard } from "@/features/dash/services/dashboard-service"
import { buildAddTilePrompt } from "@/features/dash/utils/add-tile-prompt"
import { isExplicitNewDashboardRequest } from "@/features/dash/utils/dash-request-intent"
import { trackRecentlyViewed } from "@/features/dash/utils/dashboard-helpers"
import { findDashboardBySessionId } from "@/features/dash/utils/dashboard-session"
import { buildEditTilePrompt } from "@/features/dash/utils/edit-tile-prompt"
import {
  getIncrementalEditApplicationKey,
  markIncrementalEditApplied,
  shouldApplyIncrementalResponse,
  wasIncrementalEditApplied,
} from "@/features/dash/utils/incremental-response-key"
import { isInternalDashPrompt } from "@/features/dash/utils/internal-dash-prompt"
import {
  createDashboardStreamCompiler,
  createYamlDashboardCompiler,
  extractAndApplyJsonPatch,
  extractAndApplyTileRender,
  extractAndApplyYamlDiff,
  extractAndApplyYamlEdit,
  extractDashboardSpec,
  extractPartialDashboardSpec,
  extractYamlDashboardSpec,
  getIncrementalDashboardEditFingerprint,
  hasIncompleteStaticTileAddition,
} from "@/features/dash/utils/spec-parser"
import { shouldLocalizeDashStreamError } from "@/features/dash/utils/stream-error"
import { resolveTileByPath, resolveTilePathByFlatIndex } from "@/features/dash/utils/tile-helpers"
import { getUniqueDashboardTitle } from "@/features/dash/utils/unique-dashboard-title"
import { SpreadsheetViewer } from "@/features/file-viewer/components/spreadsheet-viewer"
import { useReadSpreadsheetData } from "@/features/file-viewer/hooks/use-read-spreadsheet-data"
import { ProjectsDialog } from "@/features/projects/components/projects-dialog"
import { useCurrentSessionProject } from "@/features/projects/hooks/use-current-session-project"
import { WorkflowInputDialog } from "@/features/workflow-editor/components/workflow-input-dialog"
import { getPendingWorkflowConsumption } from "@/features/workflow-editor/utils/pending-workflow-run-guard"
import {
  createWorkflowRunPendingChatResponse,
  createWorkflowRunRequestErrorChatResponse,
  getWorkflowRunChatQuestion,
  getWorkflowRunFromApiError,
  persistWorkflowRunStatusChatResponse,
} from "@/features/workflow-editor/utils/workflow-run-chat-utils"
import { workflowRequiresInput } from "@/features/workflow-editor/utils/workflow-run-utils"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useContextMenuActions } from "@/hooks/use-context-menu-actions"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useProject, useProjectFiles, useProjects } from "@/hooks/use-projects"
import {
  useAgentModeEnabled,
  useM365Enabled,
  useNetboxEnabled,
  usePromptsEnabled,
  useServiceDeskEnabled,
  useWebSearchEnabled,
} from "@/hooks/use-route-overrides"
import { useMarkSessionNotificationViewed } from "@/hooks/use-session-sharing"
import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { useDashStore } from "@/stores/dash-store"
import { useNavigatorStore } from "@/stores/navigator-store"
import { usePendingMessageStore } from "@/stores/pending-message-store"
import { usePendingWorkflowStore } from "@/stores/pending-workflow-store"
import { useResearchPreferencesStore } from "@/stores/research-preferences-store"
import { defaultSessionState } from "@/stores/slices/chat-slice"
import { getResponseContent, getResponseContext } from "@/utils/response-format-utils"

import {
  acceptedFileTypes,
  acceptedFileTypesWithoutImages,
  azureOpenAIAcceptedFileTypes,
  azureOpenAIAcceptedFileTypesWithoutImages,
  MAX_FILES_UPLOAD,
} from "./ai-explorer-constants"

const URL_PATTERN = /https?:\/\/[^\s)"\]]+/gi

// Shared sonner toast id for the add-tile flow — the loading toast
// raised on submit is replaced in place by the success/info toast when
// the agent's reply lands.
const ADD_TILE_TOAST_ID = "dash-add-tile"
const DASH_CONTEXT_PANEL_ID = "dash-context"

function parseSourceUrls(sourceUrls: string): string[] {
  return sourceUrls
    .split(/\n|,/)
    .map((url) => url.trim())
    .filter((url) => url.length > 0)
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter((value) => value.trim().length > 0)))
}

function extractCitationTextEntries(
  dataPoints: ChatAppResponse["context"]["data_points"]
): string[] {
  if (Array.isArray(dataPoints)) {
    return dataPoints.flatMap((item) => {
      if (typeof item === "string") return [item]
      if (item && typeof item === "object") {
        const title = "title" in item && typeof item.title === "string" ? [item.title] : []
        const text = "text" in item && Array.isArray(item.text) ? item.text : []
        return [...title, ...text.filter((entry): entry is string => typeof entry === "string")]
      }
      return []
    })
  }
  if (dataPoints && typeof dataPoints === "object" && "text" in dataPoints) {
    return Array.isArray(dataPoints.text)
      ? dataPoints.text.filter((entry): entry is string => typeof entry === "string")
      : []
  }
  return []
}

function collectSourceCitations(dataPoints: ChatAppResponse["context"]["data_points"]): {
  webUrls: string[]
  libraryChunks: string[]
} {
  const entries = extractCitationTextEntries(dataPoints)
  const webUrls = uniqueStrings(
    entries.flatMap((entry) => {
      const matches = entry.match(URL_PATTERN)
      return matches ?? []
    })
  )
  const libraryChunks = uniqueStrings(
    entries.filter((entry) => !entry.match(URL_PATTERN) && entry.trim().length > 0)
  )
  return { webUrls, libraryChunks }
}

export function DashPage() {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const { getRouteChatPath } = useRoutes()
  const { projectId: urlProjectId, sessionId: urlSessionId } = useParams<{
    projectId?: string
    sessionId?: string
  }>()

  // Determine session type from current route
  const sessionType = useCurrentSessionType()

  // Get projectId from URL params first, fallback to session's projectId
  const sessionProjectId = useCurrentSessionProject()
  const projectId = urlProjectId ?? sessionProjectId ?? undefined

  // Fetch projects and project files using React Query hooks
  useProjects(sessionType)
  useProjectFiles(projectId)
  const { data: activeProject } = useProject(projectId, sessionType)

  // Fetch sessions to check if current user is a reader
  const { data: sessions = [] } = useSessionsQuery(sessionType, projectId)
  // Get config for file type restrictions
  const { data: config } = useConfigQuery()
  const useResponsesFileSearch = config?.useResponsesFileSearch ?? false

  // Get DB-backed settings for model and follow-ups
  const {
    gptModel: dbGptModel,
    availableGptModels,
    suggestFollowupQuestions,
    handleGptModelChange,
    modelSettingsHydrated,
  } = useBaseSettings({ sessionType })

  // Get namespaced stores for this sessionType
  const explorerStore = useDashStore(sessionType)

  // Sync URL sessionId → ChatService so page refresh / shared links restore the session
  useChatHistory({
    sessionType,
    useChatStore: () => explorerStore,
    projectId,
  })

  const navigatorStore = useNavigatorStore(sessionType)
  const researchPrefsStore = useResearchPreferencesStore(sessionType)

  // Preload defaults from project into stores on mount/change
  const setSourceUrls = researchPrefsStore((state) => state.setSourceUrls)
  const setTrustedMode = researchPrefsStore((state) => state.setTrustedMode)
  const sourceUrlsInput = researchPrefsStore((state) => state.prefs.sourceUrls)
  const trustedMode = researchPrefsStore((state) => state.prefs.trustedMode)
  const [showProjectsPanel, setShowProjectsPanel] = useState(false)
  const { t } = useTranslation()
  // Dash store selectors for session management
  const chatState = explorerStore((state) => {
    const currentId = state.currentSessionId
    return currentId ? state.sessionStates[currentId] || defaultSessionState : defaultSessionState
  })
  const setSelectedPrompt = explorerStore((state) => state.setSelectedPrompt)

  // Dashboard-specific state
  const dashboardMode = explorerStore((state) => state.dashboardMode)
  const setDashboardMode = explorerStore((state) => state.setDashboardMode)
  const currentDashboardSpec = explorerStore((state) => state.currentDashboardSpec)
  const setDashboardSpec = explorerStore((state) => state.setDashboardSpec)
  const setCurrentSessionIdInStore = explorerStore((state) => state.setCurrentSessionId)
  const initSession = explorerStore((state) => state.initSession)
  const setDashboardRefreshing = explorerStore((state) => state.setDashboardRefreshing)
  const dashboardPanelVisible = explorerStore((state) => state.dashboardPanelVisible)
  const setDashboardPanelVisible = explorerStore((state) => state.setDashboardPanelVisible)
  const loadDashboard = explorerStore((state) => state.loadDashboard)
  const setSelectedTileIndex = explorerStore((state) => state.setSelectedTileIndex)

  // Navigator store for libraries
  const toggleLibraryChecked = navigatorStore((state) => state.toggleLibraryChecked)
  const enabledLibraries = navigatorStore((state) => state.enabledLibraries)

  const {
    exampleQuestions,
    handleExampleClicked,
    files,
    selectedDocuments,
    filesInIndex,
    sessionFileRefs,
    handleAddFiles,
    handleResetFiles,
    handleRemoveFile,
    handleSendQuestion,
    handleCancelRequest,
    chatMessageStreamEnd,
    aborted,
    currentSessionId,
    defaultSearchServiceId,
    moduleName,
    handleStartNewChat,
    handleWebSearchChange,
    enableWebSearch,
    enableM365,
    handleM365Change,
    enableServiceDesk,
    handleServiceDeskChange,
    enableAgent,
    handleAgentChange,
    totalTokenCount,
    changeTokenCounts,
    showAgentModeInfo,
    setShowAgentModeInfo,
    showM365Info,
    isM365ApprovalLoading,
    handleM365Approve,
    handleM365Decline,
    handleM365Dismiss,
    handleContinue,
    enableHighReasoning,
    handleHighReasoningChange,
    showImageReasoningWarning,
    handleImageReasoningWarningClose,
    handleImageReasoningWarningCancel,
    isReasoningModelActive,
    hasLatestPlan,
    handleStartResearch,
    handleGraphFileSelected,
    handleDocumentsSelected,
    handleRemoveDocument,
    handleRemoveIndexedFile,
    removingFileName,
    markNextMessageAsPromptTemplate,
    enableNetbox,
    handleNetboxChange,
    reasoningMode,
    handleReasoningModeChange,
    modelOverride,
    setModelOverride,
    effortOverride,
    setEffortOverride,
  } = useDashChat()
  // Incremental dashboard edits are relative to the live spec. Reset their
  // canonical replay fence only when this page dispatches a genuinely new
  // user turn — never merely because streaming metadata toggles again.
  const lastAppliedIncrementalFingerprintRef = useRef<string | null>(null)
  // Saved dashboards hydrate historical assistant messages that are already
  // represented by the persisted spec. Ignore that history until the user
  // dispatches a new turn; clearing here is deterministic even when React
  // batches a very fast stream and no `isStreaming=true` render is observed.
  const ignoreHydratedDashboardHistoryRef = useRef(false)
  const sendDashboardQuestion = useCallback(
    (...args: Parameters<typeof handleSendQuestion>) => {
      lastAppliedIncrementalFingerprintRef.current = null
      ignoreHydratedDashboardHistoryRef.current = false

      // A user can ask for a *separate* dashboard without leaving the current
      // conversation. Establish the new-document boundary before the request
      // is built so the agent receives create intent and the post-stream
      // autosave performs POST rather than silently PUT-overwriting the open
      // dashboard. The chat session and its uploaded files stay intact.
      const question = args[0]?.question
      if (typeof question === "string" && isExplicitNewDashboardRequest(question)) {
        const live = explorerStore.getState()
        live.beginNewDashboardDocument()
        if (live.currentDashboardId || live.currentDashboardSpec) {
          // Saved dashboards open in view mode. A separate-dashboard request
          // starts a new create lifecycle, so return to chat mode before the
          // stream completes; otherwise the autosave effect intentionally
          // ignores the new spec and it disappears when the user leaves.
          setDashboardMode("chat")
          explorerStore.setState({
            currentDashboardId: null,
            currentDashboardSpec: null,
            currentDashboardTitle: "",
            currentDashboardType: "personal",
            currentDashboardQualityReview: null,
            currentDashboardQualityHistory: [],
            updateOnLoad: false,
            pendingAutoRefresh: false,
            pendingSaveAfterRefresh: false,
            selectedTileIndex: null,
            lastUpdated: null,
          })
        }
      }
      return handleSendQuestion(...args)
    },
    [explorerStore, handleSendQuestion, setDashboardMode]
  )
  const { clearUncancellableFlowRun, isCurrentSessionUncancellable, markUncancellableFlowRun } =
    useUncancellableFlowRunSessions(explorerStore, currentSessionId)

  const isContentSidebarOpen = useContentSidebarStore((s) => s.isOpen)
  const openContentPanel = useContentSidebarStore((s) => s.openPanel)
  const closeContentPanel = useContentSidebarStore((s) => s.closePanel)
  const forceCloseContentPanel = useContentSidebarStore((s) => s.forceClosePanel)
  const currentContentPanelId = useContentSidebarStore((s) => s.currentPanel?.id)
  const isContextPanelOpen = currentContentPanelId === DASH_CONTEXT_PANEL_ID
  const dashboardVisibleBeforeContextRef = useRef(false)

  const openContextPanel = useCallback(() => {
    if (dashboardPanelVisible || currentContentPanelId === "dashboard") {
      dashboardVisibleBeforeContextRef.current = true
    }

    openContentPanel({
      id: DASH_CONTEXT_PANEL_ID,
      title: t("pages.common.context"),
      content: enableAgent ? (
        <ResearchSettingsSidebar projectId={projectId} sessionType={sessionType} />
      ) : (
        <BaseSettingsSidebar projectId={projectId} sessionType={sessionType} />
      ),
      focusable: true,
      closeable: true,
      resizable: true,
      onClose: () => {
        if (!dashboardVisibleBeforeContextRef.current) return
        dashboardVisibleBeforeContextRef.current = false
        // onClose also fires when another panel REPLACES this one (openPanel
        // runs the outgoing panel's onClose). Restoring the dashboard then
        // would shove it over the panel the user just opened — defer a tick
        // and only restore when the sidebar actually ended up closed.
        queueMicrotask(() => {
          if (useContentSidebarStore.getState().currentPanel) return
          setDashboardPanelVisible(true)
        })
      },
    })
  }, [
    currentContentPanelId,
    dashboardPanelVisible,
    enableAgent,
    openContentPanel,
    projectId,
    sessionType,
    setDashboardPanelVisible,
    t,
  ])

  const contextSidebarTriggerLabel = useMemo(() => {
    if (isContextPanelOpen) {
      return t("analysispanel.close_sidebar", { defaultValue: "Close sidebar" })
    }
    return t("analysispanel.open_context", { defaultValue: "Open Context" })
  }, [isContextPanelOpen, t])

  const toggleContextPanel = useCallback(() => {
    if (isContextPanelOpen) {
      closeContentPanel()
      return
    }
    openContextPanel()
  }, [closeContentPanel, isContextPanelOpen, openContextPanel])

  useEffect(() => {
    if (isContextPanelOpen) {
      openContextPanel()
    }
  }, [isContextPanelOpen, openContextPanel])

  // Check if current user is a reader (cannot send messages)
  const currentSession = useMemo(
    () => sessions.find((s) => s != null && s.id === currentSessionId),
    [sessions, currentSessionId]
  )
  const isReader = currentSession?.currentUserRole === "reader"

  // Get current user's access mode from session participants
  const currentUserOid = SettingsService.getInstance().claims.oid
  const currentUserParticipant = useMemo(() => {
    if (!currentSession?.participants || !currentUserOid) return undefined

    // Get user's groups from settings service
    const userGroups = SettingsService.getInstance().claims.groups || []
    const userGroupsSet = new Set(userGroups)

    // Find participant: check direct user OR group membership
    return currentSession.participants.find(
      (p) =>
        (p.type === "user" && p.oid === currentUserOid) ||
        (p.type === "group" && userGroupsSet.has(p.oid))
    )
  }, [currentSession?.participants, currentUserOid])
  const currentUserAccessMode = currentUserParticipant?.messageAccessMode

  // Get combined answers for display (messages + streamingAnswer if exists)
  const answersForSpreadsheet = useMemo(
    () =>
      chatState.streamingAnswer
        ? [...chatState.messages, chatState.streamingAnswer]
        : chatState.messages,
    [chatState.messages, chatState.streamingAnswer]
  )

  const { sharedHistoryMarkers } = useSharedHistoryMarkers(
    answersForSpreadsheet,
    currentSession?.participants,
    currentUserParticipant
  )

  // Get current message ID (from latest message in chat)
  const currentMessageId = useMemo(() => {
    if (answersForSpreadsheet.length === 0) return undefined
    const latestMessage = answersForSpreadsheet[answersForSpreadsheet.length - 1]
    if (!latestMessage || !Array.isArray(latestMessage) || latestMessage.length < 2) {
      return undefined
    }
    const response: ChatAppResponse = latestMessage[1]
    return response.messageId ?? getResponseContext(response).message_id ?? undefined
  }, [answersForSpreadsheet])

  // Mark notification as viewed when user opens a shared session
  const markNotificationViewed = useMarkSessionNotificationViewed()
  const markedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      currentSessionId &&
      currentSession?.hasUnviewedNotification &&
      markedSessionRef.current !== currentSessionId
    ) {
      markedSessionRef.current = currentSessionId
      markNotificationViewed.mutate(currentSessionId)
    }
    // Reset ref when session changes (so we can mark new session if needed)
    if (
      currentSessionId &&
      markedSessionRef.current !== currentSessionId &&
      !currentSession?.hasUnviewedNotification
    ) {
      markedSessionRef.current = currentSessionId
    }
  }, [currentSessionId, currentSession?.hasUnviewedNotification, markNotificationViewed])

  const { spreadSheetData, spreadSheetChatIndex } = useReadSpreadsheetData({
    files,
    chatIndex: answersForSpreadsheet.length,
    sessionId: currentSessionId,
  })

  const getMessageKey = useCallback(
    (response: ChatAppResponse, prefix: string, index: number) => {
      const responseId = response.messageId ?? response.context?.message_id
      if (responseId) {
        return responseId
      }
      return `${currentSessionId ?? "session"}-${prefix}-${index}`
    },
    [currentSessionId]
  )

  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareMessageId, setShareMessageId] = useState<string | undefined>()
  const handleManageSharing = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  // Use route-aware feature checks
  const hasAgentModeFeature = useAgentModeEnabled()
  const isPromptsFeatureEnabled = usePromptsEnabled()
  const showWebSearchOption = useWebSearchEnabled()
  const showM365Option = useM365Enabled()
  const showServiceDeskOption = useServiceDeskEnabled()
  const showNetboxOption = useNetboxEnabled()

  // Voice mode consent modal state
  const [showVoiceModeInfo, setShowVoiceModeInfo] = useState(false)
  const toggleVoiceModeRef = useRef<(() => void) | null>(null)

  // Callback to check voice mode consent before starting
  const handleBeforeVoiceModeStart = useCallback((): boolean => {
    if (hasVoiceInternalDataConsent()) {
      return true
    }
    // Show modal and prevent voice mode from starting
    setShowVoiceModeInfo(true)
    return false
  }, [])

  // Names the provider in the consent modal's copy.
  const voiceEngine = useVoiceEngine()

  // Called when user accepts the voice mode consent modal
  const handleVoiceModeAccept = useCallback(() => {
    // Start voice mode after consent is given
    toggleVoiceModeRef.current?.()
  }, [])

  // Store the toggle function from ChatFooter
  const handleGetToggleVoiceMode = useCallback((toggleFn: () => void) => {
    toggleVoiceModeRef.current = toggleFn
  }, [])

  const handleStartVoiceModeFromTools = useCallback(() => {
    const canStart = handleBeforeVoiceModeStart()
    if (!canStart) return
    toggleVoiceModeRef.current?.()
  }, [handleBeforeVoiceModeStart])

  // State for exposing setQuestion function from Chat.Footer
  const [setQuestionFunction, setSetQuestionFunction] = useState<((text: string) => void) | null>(
    null
  )

  // Handle pending message from "break out to new chat"
  const pendingMessage = usePendingMessageStore((state) => state.pendingMessage)
  const clearPendingMessage = usePendingMessageStore((state) => state.clearPendingMessage)

  // Handle pending workflow from sidebar click
  const pendingWorkflow = usePendingWorkflowStore((state) => state.pendingWorkflow)
  const clearPendingWorkflow = usePendingWorkflowStore((state) => state.clearPendingWorkflow)
  const handledPendingWorkflowIdRef = useRef<string | null>(null)
  const [workflowToRun, setWorkflowToRun] = useState<WorkflowItem | null>(null)

  // Context menu actions for break out functionality
  const { handleBreakOutChat: contextMenuBreakOut } = useContextMenuActions({
    currentSessionId,
    onSendQuestion: sendDashboardQuestion,
    sessionType: sessionType,
    projectId,
  })

  // Handler for breaking out from AI response button
  const handleBreakOutFromAIResponse = useCallback(
    async (content: string) => {
      try {
        logger.info("Break out button clicked", { contentLength: content.length })
        await contextMenuBreakOut(content, "user")
      } catch (error) {
        logger.error("Break out button failed", error as Error)
        toast.error(t("dash.chat.create_error", { defaultValue: "Failed to create new chat" }))
      }
    },
    [contextMenuBreakOut, t]
  )

  useEffect(() => {
    // Wait for the new chat to be fully loaded
    // Check if we have a pending message AND we're in a different session
    if (
      pendingMessage &&
      (currentSessionId !== pendingMessage.sourceSessionId || currentSessionId === null)
    ) {
      // Small delay to ensure the chat is fully rendered
      const timer = setTimeout(() => {
        if (pendingMessage.messageType === "assistant") {
          // For assistant messages, inject directly into chat state
          logger.info("Processing pending assistant message", {
            textLength: pendingMessage.text.length,
            sourceSessionId: pendingMessage.sourceSessionId,
          })
          const statusLine = pendingMessage.sourceSessionId
            ? `🔗 ${t("dash.chat.breakout_from", { defaultValue: "Broken out from" })} [${pendingMessage.sourceSessionTitle || t("dash.chat.previous_chat", { defaultValue: "previous chat" })}](/dash/chat/${pendingMessage.sourceSessionId})\n\n`
            : `🔗 ${t("dash.chat.breakout_from", { defaultValue: "Broken out from" })} ${t("dash.chat.previous_chat", { defaultValue: "previous chat" })}\n\n`
          const displayContent = statusLine + pendingMessage.text

          // Inject as an AI response
          const updateChatState = explorerStore.getState().changeChatState
          // Read chatState inside changeChatState to avoid overwriting concurrent updates
          const currentSessId = explorerStore.getState().currentSessionId
          const currentMessages = currentSessId
            ? (explorerStore.getState().sessionStates[currentSessId]?.messages ?? [])
            : []
          updateChatState({
            messages: [
              ...currentMessages,
              [
                "", // Empty user message
                {
                  content: displayContent,
                  context: {
                    data_points: [],
                    followup_questions: null,
                    thoughts: [],
                  },
                  session_state: null,
                  completed: true,
                },
              ],
            ],
          })

          toast.info(t("dash.chat.created", { defaultValue: "Chat created" }), {
            description: t("dash.chat.breakout_created", {
              defaultValue: 'Broken out from "{{title}}"',
              title:
                pendingMessage.sourceSessionTitle ||
                t("dash.chat.previous_chat", { defaultValue: "previous chat" }),
            }),
          })
        } else {
          // For user messages, populate the chat input if available
          if (setQuestionFunction) {
            setQuestionFunction(pendingMessage.text)
            if (pendingMessage.sourceSessionTitle) {
              toast.info(t("dash.chat.created", { defaultValue: "Chat created" }), {
                description: t("dash.chat.created_from", {
                  defaultValue: 'Created from "{{title}}"',
                  title: pendingMessage.sourceSessionTitle,
                }),
              })
            }
          }
        }
        // Clear the pending message
        clearPendingMessage()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [pendingMessage, setQuestionFunction, currentSessionId, clearPendingMessage])

  const runWorkflowViaApi = useCallback(
    async (
      workflow: WorkflowItem,
      question = "",
      userInputData?: Record<string, string | File[]>
    ) => {
      const userText = getWorkflowRunChatQuestion(workflow, question)
      // Only text (string) fields are JSON-safe for the run POST; file fields
      // route through the chat path above. This branch only runs when no files.
      const textInputData = userInputData
        ? Object.fromEntries(
            Object.entries(userInputData).filter(
              (entry): entry is [string, string] => typeof entry[1] === "string"
            )
          )
        : undefined
      const hasTextInputData = !!textInputData && Object.keys(textInputData).length > 0
      const ensureRunSession = async (chatService: ChatService): Promise<string | null> => {
        let currentSessId = explorerStore.getState().currentSessionId

        if (!currentSessId) {
          const newSession = await chatService.createSession(
            sessionType,
            userText.slice(0, 50),
            undefined,
            false,
            undefined,
            projectId
          )
          if (newSession?.id) {
            currentSessId = newSession.id
            const sessionHistoryKey = queryKeys.sessionHistory(sessionType, projectId)
            explorerStore.getState().setCurrentSessionId(newSession.id)
            explorerStore.getState().initSession(newSession.id)
            await queryClient.cancelQueries({ queryKey: sessionHistoryKey })
            queryClient.setQueryData(
              sessionHistoryKey,
              (oldSessions: ChatSessionModel[] | undefined) => {
                const sessions = oldSessions ?? []
                const remainingSessions = sessions.filter(
                  (existingSession) => existingSession?.id !== newSession.id
                )
                return [newSession, ...remainingSessions]
              }
            )
            void navigate(`${getRouteChatPath(sessionType)}/${newSession.id}`)
          }
        }

        return currentSessId
      }

      const appendChatResponse = async (response: ChatAppResponse) => {
        const chatService = ChatService.getInstance()
        const currentSessId = await ensureRunSession(chatService)
        if (!currentSessId) {
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedNoSession"),
          })
          return false
        }

        try {
          await chatService.saveWorkflowRunMessage(currentSessId, userText, response)
        } catch (error) {
          logger.warn("Failed to persist workflow run chat message", error as Error)
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedInspectHistory"),
          })
          return false
        }

        const currentMessages =
          explorerStore.getState().sessionStates[currentSessId]?.messages ?? []
        explorerStore.getState().changeChatState({
          messages: [...currentMessages, [userText, response]],
          streamingAnswer: null,
          isLoading: false,
          isStreaming: false,
        })
        void navigate(`${getRouteChatPath(sessionType)}/${currentSessId}`, { replace: true })
        return true
      }

      const appendRunMessage = async (run: FlowRun) => {
        const chatService = ChatService.getInstance()
        const currentSessId = await ensureRunSession(chatService)
        if (!currentSessId) {
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedNoSession"),
          })
          return false
        }

        try {
          const runResponse = await persistWorkflowRunStatusChatResponse({
            sessionId: currentSessId,
            userText,
            run,
            saveWorkflowRunMessage: chatService.saveWorkflowRunMessage.bind(chatService),
          })
          const currentMessages =
            explorerStore.getState().sessionStates[currentSessId]?.messages ?? []
          explorerStore.getState().changeChatState({
            messages: [...currentMessages, [userText, runResponse]],
            streamingAnswer: null,
            isLoading: false,
            isStreaming: false,
          })
          void navigate(`${getRouteChatPath(sessionType)}/${currentSessId}`, { replace: true })
          return true
        } catch (error) {
          logger.warn("Failed to persist workflow run chat message", error as Error)
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedInspectHistory"),
          })
          return false
        }
      }

      const showPendingRunMessage = async (): Promise<string | null> => {
        const chatService = ChatService.getInstance()
        const currentSessId = await ensureRunSession(chatService)
        if (!currentSessId) return null

        markUncancellableFlowRun(currentSessId)
        const pendingResponse = createWorkflowRunPendingChatResponse(workflow)
        explorerStore.getState().changeChatState({
          streamingAnswer: [userText, pendingResponse],
          isLoading: true,
          isStreaming: true,
          canContinue: false,
        })
        void navigate(`${getRouteChatPath(sessionType)}/${currentSessId}`, { replace: true })
        return currentSessId
      }

      let runSessionId: string | null = null
      try {
        runSessionId = await showPendingRunMessage()
        const run = (await api.post(`/explorer/workflow/${workflow.id}/run`, {
          question,
          ...(hasTextInputData ? { user_input_data: textInputData } : {}),
        })) as unknown as FlowRun

        if (run.status === "failed") {
          await appendRunMessage(run)
          toast.error(t("workflowLanding.toast.runFailed"), {
            description: run.error ?? workflow.title,
          })
          return
        }

        if (run.status === "paused") {
          await appendRunMessage(run)
          toast.info(t("workflowLanding.toast.runPaused"), {
            description: workflow.title,
          })
          return
        }

        if (run.status === "queued" || run.status === "running") {
          await appendRunMessage(run)
          toast.info(
            run.status === "queued"
              ? t("workflowLanding.toast.runQueued")
              : t("workflowLanding.toast.runRunning"),
            {
              description: t("workflowLanding.toast.runInspectProgress"),
            }
          )
          return
        }

        await appendRunMessage(run)

        toast.success(t("workflowLanding.toast.runCompleted"), {
          description: workflow.title,
        })
      } catch (error) {
        const failedRun = getWorkflowRunFromApiError(error)
        if (failedRun) {
          await appendRunMessage(failedRun)
        } else {
          await appendChatResponse(
            createWorkflowRunRequestErrorChatResponse({
              workflowId: workflow.id,
              workflowTitle: workflow.title,
              error,
            })
          )
        }
        toast.error(t("workflowLanding.toast.runFailed"), {
          description: error instanceof Error ? error.message : workflow.title,
        })
      } finally {
        if (runSessionId) clearUncancellableFlowRun(runSessionId)
      }
    },
    [
      clearUncancellableFlowRun,
      explorerStore,
      getRouteChatPath,
      markUncancellableFlowRun,
      navigate,
      projectId,
      queryClient,
      sessionType,
      t,
    ]
  )

  // Handle pending workflow from sidebar - show input dialog or run immediately
  useEffect(() => {
    const consumption = getPendingWorkflowConsumption(
      pendingWorkflow?.id,
      handledPendingWorkflowIdRef.current
    )
    handledPendingWorkflowIdRef.current = consumption.nextHandledWorkflowId

    if (!pendingWorkflow || !consumption.shouldConsume) return
    clearPendingWorkflow()

    if (workflowRequiresInput(pendingWorkflow)) {
      // Show the input dialog
      setWorkflowToRun(pendingWorkflow)
    } else {
      void runWorkflowViaApi(pendingWorkflow)
    }
  }, [pendingWorkflow, clearPendingWorkflow, runWorkflowViaApi])

  // Handle workflow run from input dialog
  const handleWorkflowRun = useCallback(
    async (data: {
      question: string
      files?: File[]
      user_input_data?: Record<string, string | File[]>
    }) => {
      if (!workflowToRun) return

      // Only text (string) START fields ride in user_input_data JSON; file
      // fields go through the upload pipeline below. Never drop the text fields
      // just because files are present (#2176).
      const textInputData = data.user_input_data
        ? Object.fromEntries(
            Object.entries(data.user_input_data).filter(
              (entry): entry is [string, string] => typeof entry[1] === "string"
            )
          )
        : undefined
      const hasTextInputData = !!textInputData && Object.keys(textInputData).length > 0

      if (data.files && data.files.length > 0) {
        handleAddFiles(data.files)
        await sendDashboardQuestion({
          question: data.question,
          agentId: workflowToRun.id,
          files: data.files,
          ...(hasTextInputData ? { user_input_data: textInputData } : {}),
        })
      } else {
        await runWorkflowViaApi(workflowToRun, data.question, data.user_input_data)
      }

      // Close the dialog
      setWorkflowToRun(null)
    },
    [workflowToRun, handleAddFiles, sendDashboardQuestion, runWorkflowViaApi]
  )

  // Memoize expensive computations
  const maxTokens = useMemo(() => dbGptModel?.input_token_limit ?? 0, [dbGptModel])

  const dropzoneOptions = useMemo(() => {
    // Choose base file types based on Azure OpenAI Responses API usage
    const baseTypes = useResponsesFileSearch ? azureOpenAIAcceptedFileTypes : acceptedFileTypes
    const baseTypesNoImages = useResponsesFileSearch
      ? azureOpenAIAcceptedFileTypesWithoutImages
      : acceptedFileTypesWithoutImages

    return {
      accept:
        isReasoningModelActive && !(dbGptModel?.feature_flags.supports_images ?? true)
          ? baseTypesNoImages
          : baseTypes,
    }
  }, [isReasoningModelActive, dbGptModel, useResponsesFileSearch])

  // Stable callback for onSetQuestion
  const handleSetQuestion = useCallback((setter: (text: string) => void) => {
    setSetQuestionFunction(() => setter) // Use functional update
  }, [])

  const handleTryAgainAction = useCallback(
    (question: string) => sendDashboardQuestion({ question }),
    [sendDashboardQuestion]
  )

  const handleSendFollowUp = useCallback(
    async ({ question, forceWebSearch }: { question: string; forceWebSearch?: boolean }) => {
      if (!question || !question.trim()) return
      if (forceWebSearch) {
        handleWebSearchChange(true)
      }
      await sendDashboardQuestion({ question, forceWebSearch })
    },
    [sendDashboardQuestion, handleWebSearchChange]
  )
  const handleFollowupQuestionClicked = useCallback(
    (question: string) => sendDashboardQuestion({ question }),
    [sendDashboardQuestion]
  )

  // Ref to hold the sendSessionContext function from VoiceInput
  const sendSessionContextRef = useRef<((sessionId: string) => void) | null>(null)

  // Callback to receive sendSessionContext function from VoiceInput
  const handleGetSendSessionContext = useCallback((sendFn: (sessionId: string) => void) => {
    sendSessionContextRef.current = sendFn
  }, [])

  // Handler for voice message transcripts
  const handleVoiceMessage = useCallback(
    async (userText: string, response: ChatAppResponse, backendSessionId?: string) => {
      const chatService = ChatService.getInstance()
      let currentSessId = explorerStore.getState().currentSessionId

      // Reuse the backend-created container for the first realtime turn. It is
      // already the artifact/session scope used by voice tools, so creating a
      // second page-owned session would split one conversation across two ids.
      if (!currentSessId && backendSessionId) {
        currentSessId = backendSessionId
        explorerStore.getState().setCurrentSessionId(backendSessionId)
        explorerStore.getState().initSession(backendSessionId)
        void navigate(`${getRouteChatPath(sessionType)}/${backendSessionId}`)
        logger.info("Adopted backend-created voice chat session", {
          sessionId: backendSessionId,
        })
      }

      // Create a session if none exists
      if (!currentSessId) {
        try {
          const titleForSession = userText.slice(0, 50)
          const newSession = await chatService.createSession(
            sessionType,
            titleForSession,
            undefined,
            false,
            undefined,
            projectId,
            "voice" // Icon type for voice mode sessions
          )
          if (newSession?.id) {
            currentSessId = newSession.id
            explorerStore.getState().setCurrentSessionId(newSession.id)
            explorerStore.getState().initSession(newSession.id)
            // Navigate to the new session
            void navigate(`${getRouteChatPath(sessionType)}/${newSession.id}`)
            logger.info("Created new voice chat session", { sessionId: newSession.id })

            // CRITICAL: Send session_id to backend via WebSocket immediately
            // This ensures the backend saves the conversation to this session, not a new one
            if (sendSessionContextRef.current) {
              sendSessionContextRef.current(newSession.id)
            }
          }
        } catch (error) {
          logger.error("Failed to create voice chat session", error as Error)
          return
        }
      }

      // Add the message to the chat store (backend saves via WebSocket)
      const changeChatState = explorerStore.getState().changeChatState
      const currentMessages = currentSessId
        ? (explorerStore.getState().sessionStates[currentSessId]?.messages ?? [])
        : []
      changeChatState({
        messages: [...currentMessages, [userText, response]],
      })
    },
    [explorerStore, getRouteChatPath, navigate, projectId, sessionType]
  )

  // Handler for streaming voice message (real-time text display)
  const handleStreamingVoiceMessage = useCallback(
    (userText: string, response: ChatAppResponse) => {
      const changeChatState = explorerStore.getState().changeChatState
      changeChatState({
        streamingAnswer: [userText, response],
        isStreaming: true,
      })
    },
    [explorerStore]
  )

  // Handler to clear streaming state when voice response is complete
  const handleStreamingComplete = useCallback(() => {
    const changeChatState = explorerStore.getState().changeChatState
    changeChatState({
      streamingAnswer: null,
      isStreaming: false,
    })
    // The agent may have called dash_save_bindings during the turn — those
    // writes land on the backend doc.query_bindings envelope, not on the
    // streamed spec. Re-hydrate queryBindings from the server so the
    // tile-binding badges flip from "AI refresh" to "Live SQL" without a
    // full page reload.
    const state = explorerStore.getState()
    const dashboardId = state.currentDashboardId
    if (!dashboardId) return
    void DashboardService.getDashboard(dashboardId)
      .then((fresh) => {
        const latestState = explorerStore.getState()
        const spec = latestState.currentDashboardSpec
        if (!spec) return
        const nextSpec: DashboardSpec = {
          ...spec,
          queryBindings: fresh.query_bindings ?? spec.queryBindings,
        }
        latestState.setDashboardSpec(nextSpec)
      })
      .catch((err) => {
        logger.warn("Failed to re-hydrate bindings after streaming", err as Error)
      })
  }, [explorerStore])

  const handleSwitchModelAction = useCallback(
    (modelId: string) => {
      const model = availableGptModels.find((item) => item.id === modelId)
      if (model) {
        handleGptModelChange(model)
      } else {
        openContextPanel()
      }
    },
    [availableGptModels, handleGptModelChange, openContextPanel]
  )

  // Callback to refresh messages after update
  const handleMessageUpdate = useCallback(async () => {
    if (!currentSessionId) return

    try {
      const chatService = ChatService.getInstance()
      const messages = await chatService.getMessages(currentSessionId)

      // Helper to add message ID to response
      const addMessageId = (response: ChatAppResponse, messageId: string): ChatAppResponse => {
        return {
          ...response,
          messageId, // Add to top level for Canvas editing
          context: {
            ...response.context,
            message_id: messageId, // Also in context for backward compatibility
          },
        }
      }

      // Helper to ensure activities array exists
      const ensureActivitiesArray = (response: ChatAppResponse) => {
        if (!response.activities) {
          return { ...response, activities: [] }
        }
        return response
      }

      const answers: [string, ChatAppResponse][] = messages.reduce(
        (acc, message) => {
          try {
            const content = message.content as unknown as
              | [string, ChatAppResponse]
              | [string, ChatAppResponse][]

            if (Array.isArray(content)) {
              if (content.length > 0) {
                if (
                  content.length === 2 &&
                  typeof content[0] === "string" &&
                  typeof content[1] === "object"
                ) {
                  // Single interaction format: [question, response]
                  const responseObj = content[1] as ChatAppResponse
                  const responseWithId = addMessageId(responseObj, message.id)
                  return acc.concat([[content[0], ensureActivitiesArray(responseWithId)]])
                } else {
                  // Array of interactions format
                  const processedContent = content.map((item) => {
                    if (Array.isArray(item)) {
                      const responseWithId = addMessageId(item[1] as ChatAppResponse, message.id)
                      return [item[0], ensureActivitiesArray(responseWithId)]
                    }
                    return item
                  })
                  return acc.concat(processedContent as [string, ChatAppResponse][])
                }
              }
            }
            return acc
          } catch {
            // Silently handle errors during message processing
            return acc
          }
        },
        [] as [string, ChatAppResponse][]
      )

      // Update chat state with refreshed messages
      const changeChatState = explorerStore.getState().changeChatState
      changeChatState({ messages: answers })
    } catch {
      // Silently handle errors during refresh
    }
  }, [currentSessionId])

  // Reset citations when projectId context changes
  useEffect(() => {
    navigatorStore.getState().setCitations([])
    navigatorStore.getState().setActiveCitations([])
  }, [projectId, navigatorStore])

  const showHighReasoning =
    availableGptModels.some((model: GPTModelItem) => model.id === "gpt-5_eu") && !enableAgent
  // Mirror AI Explorer: the ReasoningModePill in the composer footer
  // doubles as the model picker for reasoning-capable models, replacing
  // the legacy high-reasoning toggle.
  const reasoningCapableModels = availableGptModels.filter(
    (model: GPTModelItem) => model.feature_flags?.supports_reasoning_efforts
  )
  const showReasoningMode = reasoningCapableModels.length > 0
  const selectedReasoningModel: GPTModelItem | undefined = modelOverride
    ? (availableGptModels.find((model: GPTModelItem) => model.id === modelOverride) ?? dbGptModel)
    : (dbGptModel ?? availableGptModels.find((model: GPTModelItem) => model.id === "auto"))

  // Function to apply prompt settings when a prompt is selected
  const applyPromptSettings = useCallback(
    (prompt: PromptLibraryItem) => {
      // Apply web search setting
      if (prompt.defaultWebSearch !== undefined) {
        handleWebSearchChange(prompt.defaultWebSearch)
      }

      // Apply trusted sources
      if (
        prompt.useTrustedSources &&
        prompt.trustedSourceUrls &&
        prompt.trustedSourceUrls.length > 0
      ) {
        setSourceUrls(prompt.trustedSourceUrls.join("\n"))
        if (prompt.trustedMode) {
          setTrustedMode(prompt.trustedMode)
        }
      }

      // Apply library settings
      if (prompt.enabledIndexIds && prompt.enabledIndexIds.length > 0) {
        const availableLibraryIds = Object.keys(enabledLibraries)
        const enabledIds = prompt.enabledIndexIds

        availableLibraryIds.forEach((libraryId) => {
          const isCurrentlyEnabled = enabledLibraries[libraryId].checked
          const shouldBeEnabled = enabledIds.includes(libraryId)

          if (isCurrentlyEnabled !== shouldBeEnabled) {
            toggleLibraryChecked(libraryId)
          }
        })
      }

      // Apply AI model setting
      if (prompt.defaultModelId) {
        const model = availableGptModels.find((m) => m.id === prompt.defaultModelId)
        if (model) {
          handleGptModelChange(model)

          // Apply high reasoning setting with the new model
          if (prompt.defaultHighReasoning !== undefined) {
            handleHighReasoningChange(prompt.defaultHighReasoning, model)
          }
        }
      } else if (prompt.defaultHighReasoning !== undefined) {
        // No model change, use current model
        handleHighReasoningChange(prompt.defaultHighReasoning)
      }
    },
    [
      handleWebSearchChange,
      setSourceUrls,
      setTrustedMode,
      enabledLibraries,
      toggleLibraryChecked,
      handleHighReasoningChange,
      availableGptModels,
      handleGptModelChange,
    ]
  )

  // ─── Dashboard spec extraction from chat responses ─────────────────
  const enrichDashboardSpec = useCallback(
    (nextSpec: DashboardSpec, response: ChatAppResponse, sourcePrompt?: string): DashboardSpec => {
      const enabledLibraryIds = Object.entries(enabledLibraries)
        .filter(([, library]) => library.checked)
        .map(([libraryId]) => libraryId)
      const citations = collectSourceCitations(getResponseContext(response).data_points)
      const previousCitations = currentDashboardSpec?.sourceContext?.citations
      const mergedCitations = {
        webUrls: uniqueStrings([...(previousCitations?.webUrls ?? []), ...citations.webUrls]),
        libraryChunks: uniqueStrings([
          ...(previousCitations?.libraryChunks ?? []),
          ...citations.libraryChunks,
        ]),
      }

      const previousSourcePrompt = currentDashboardSpec?.sourcePrompt
      const nextSourcePrompt = nextSpec.sourcePrompt
      const userSourcePrompt = sourcePrompt

      return {
        ...nextSpec,
        sourcePrompt:
          (previousSourcePrompt && !isInternalDashPrompt(previousSourcePrompt)
            ? previousSourcePrompt
            : undefined) ??
          (nextSourcePrompt && !isInternalDashPrompt(nextSourcePrompt)
            ? nextSourcePrompt
            : undefined) ??
          (userSourcePrompt && !isInternalDashPrompt(userSourcePrompt)
            ? userSourcePrompt
            : undefined),
        dashboardSessionId:
          currentDashboardSpec?.dashboardSessionId ??
          currentSessionId ??
          nextSpec.dashboardSessionId,
        sourceContext: {
          ...currentDashboardSpec?.sourceContext,
          searchServices: enabledLibraryIds,
          sourceUrls: parseSourceUrls(sourceUrlsInput),
          trustedMode,
          documentIds: selectedDocuments.map((document) => document.id),
          citations: mergedCitations,
        },
      }
    },
    [
      currentDashboardSpec?.dashboardSessionId,
      currentDashboardSpec?.sourceContext,
      currentDashboardSpec?.sourcePrompt,
      currentSessionId,
      enabledLibraries,
      selectedDocuments,
      sourceUrlsInput,
      trustedMode,
    ]
  )

  const ensureDashboardSession = useCallback(
    async (dashboard: SavedDashboard): Promise<string | null> => {
      const chatService = ChatService.getInstance()
      const dashboardSpec = dashboard.spec as DashboardSpec
      const storedSessionId = dashboardSpec.dashboardSessionId?.trim()
      let targetSession = storedSessionId
        ? sessions.find((session) => session?.id === storedSessionId)
        : undefined

      if (!targetSession && storedSessionId) {
        targetSession = chatService.getSessionById(storedSessionId)
      }

      if (!targetSession) {
        const createdSession = await chatService.createSession(
          sessionType,
          dashboard.title || t("dash.dashboard.untitled"),
          undefined,
          false,
          undefined,
          projectId
        )
        if (!createdSession?.id) {
          return null
        }
        targetSession = createdSession
        if (storedSessionId) {
          toast.info(
            t("dash.dashboard.session_recreated", {
              defaultValue:
                "Original chat session could not be found. A new session has been created.",
            })
          )
        }
      }

      if (!targetSession.id) {
        return null
      }

      chatService.openSession(sessionType, targetSession)
      initSession(targetSession.id)
      setCurrentSessionIdInStore(targetSession.id)
      void navigate(`${getRouteChatPath(sessionType)}/${targetSession.id}`)
      return targetSession.id
    },
    [
      getRouteChatPath,
      initSession,
      navigate,
      projectId,
      sessionType,
      sessions,
      setCurrentSessionIdInStore,
      t,
    ]
  )

  const attachDashboardSessionId = useCallback(
    async (dashboard: SavedDashboard): Promise<DashboardSpec> => {
      // query_bindings live on the envelope, not inside the stored spec JSON.
      // Merge them back onto the runtime spec so refresh-gate checks
      // (spec.queryBindings?.length) can see them — but NEVER persist them
      // inside spec; the envelope remains canonical.
      const storedSpec = dashboard.spec as DashboardSpec
      const runtimeSpec: DashboardSpec = {
        ...storedSpec,
        queryBindings: dashboard.query_bindings ?? storedSpec.queryBindings,
      }
      const sessionId = await ensureDashboardSession(dashboard)
      if (!sessionId) {
        return runtimeSpec
      }

      const nextRuntimeSpec: DashboardSpec = {
        ...runtimeSpec,
        dashboardSessionId: sessionId,
      }

      if (storedSpec.dashboardSessionId !== sessionId) {
        // Persist only the session id — strip the merged envelope bindings
        // so they stay on doc.query_bindings rather than duplicating inside
        // spec and drifting over time.
        // Strip queryBindings from the persisted spec — they live on
        // doc.query_bindings, not inside spec, to avoid duplication drift.
        const persistSpec = { ...nextRuntimeSpec }
        delete (persistSpec as { queryBindings?: unknown }).queryBindings
        void DashboardService.updateDashboard(dashboard.id, {
          spec: persistSpec as Record<string, unknown>,
        }).catch((error) => {
          logger.warn("Failed to persist dashboard session binding", error as Error)
        })
      }

      return nextRuntimeSpec
    },
    [ensureDashboardSession]
  )

  // Use a ref for enrichDashboardSpec so the effect below doesn't depend on it
  // (enrichDashboardSpec reads currentDashboardSpec, which setDashboardSpec updates,
  //  so putting it in the deps array would create an infinite render loop).
  const enrichDashboardSpecRef = useRef(enrichDashboardSpec)
  enrichDashboardSpecRef.current = enrichDashboardSpec

  // Same pattern for currentDashboardSpec — needed inside the effect for yaml-edit
  // merging, but adding it to deps would create an infinite loop (effect sets spec → store
  // updates currentDashboardSpec → effect re-fires → ∞).
  const currentDashboardSpecRef = useRef(currentDashboardSpec)
  currentDashboardSpecRef.current = currentDashboardSpec

  const streamCompilerRef = useRef(createDashboardStreamCompiler())
  const yamlCompilerRef = useRef(createYamlDashboardCompiler())
  const wasStreamingRef = useRef(false)
  // Tracks the chat-message content the autosave path most recently
  // refreshed against. The extract effect uses this to recognise
  // "I'm being re-fired for the same message that already produced
  // a refresh commit" and skip the re-write, which would otherwise
  // strip queryBindings + refreshed values off the live store.
  const lastRefreshedContentRef = useRef<string | null>(null)
  // Incremental fences are relative to the current spec. Chat message metadata
  // can settle through several renders with identical response content; apply
  // each completed turn's patch/diff only once or an `add` operation duplicates
  // the same tile on every settle render.
  // Opening a saved dashboard also hydrates its chat history. That history has
  // already been folded into the persisted spec, so replaying the latest
  // patch/diff against the saved spec duplicates additive edits. Ignore
  // completed history until a genuinely new streamed response starts.
  // AI refreshes stay in view mode, so the normal chat-mode autosave does not
  // run. Persist the exact completed response spec from the extraction effect.
  const saveRefreshedDashboardRef = useRef<(spec: DashboardSpec) => void>(() => undefined)

  // Extract dashboard spec from the latest AI response (streaming or completed)
  useEffect(() => {
    if (dashboardMode === "landing") return

    // Reset compilers at the start of a new streaming session (false → true transition)
    if (chatState.isStreaming) {
      ignoreHydratedDashboardHistoryRef.current = false
      if (!wasStreamingRef.current) {
        streamCompilerRef.current.reset()
        yamlCompilerRef.current.reset()
      }
    }
    wasStreamingRef.current = chatState.isStreaming

    const allAnswers = chatState.streamingAnswer
      ? [...chatState.messages, chatState.streamingAnswer]
      : chatState.messages
    const latestAnswer = allAnswers.at(-1)
    if (!latestAnswer?.[1]) return

    const content = getResponseContent(latestAnswer[1])
    if (!content) return
    if (!chatState.isStreaming && ignoreHydratedDashboardHistoryRef.current) return

    let spec: DashboardSpec | null = null
    let appliedIncrementalEdit = false
    let appliedIncrementalFingerprint: string | null = null
    let appliedIncrementalApplicationKey: string | null = null

    if (chatState.isStreaming) {
      // Prioritetsordning vid streaming:
      // 1. json-render-stream (JSONL)
      // 2. yaml-render (property-level streaming)
      // 3. json-render (partial JSON)
      spec = streamCompilerRef.current.update(content)
      if (!spec) spec = yamlCompilerRef.current.update(content)
      if (!spec) spec = extractPartialDashboardSpec(content)
    } else {
      // Complete response: try all formats
      spec = extractDashboardSpec(content)
      if (!spec) spec = extractYamlDashboardSpec(content)
      const yamlEditFingerprint = getIncrementalDashboardEditFingerprint(content, "yaml-edit")
      const yamlEditApplicationKey = yamlEditFingerprint
        ? getIncrementalEditApplicationKey(
            currentSessionId,
            allAnswers.length - 1,
            yamlEditFingerprint
          )
        : null
      if (
        !spec &&
        currentDashboardSpecRef.current &&
        !wasIncrementalEditApplied(currentDashboardSpecRef.current, yamlEditApplicationKey) &&
        shouldApplyIncrementalResponse(
          lastAppliedIncrementalFingerprintRef.current,
          yamlEditFingerprint
        )
      ) {
        spec = extractAndApplyYamlEdit(content, currentDashboardSpecRef.current)
        appliedIncrementalEdit = spec !== null
        if (appliedIncrementalEdit) {
          appliedIncrementalFingerprint = yamlEditFingerprint
          appliedIncrementalApplicationKey = yamlEditApplicationKey
        }
      }
      const jsonPatchFingerprint = getIncrementalDashboardEditFingerprint(content, "yaml-patch")
      const jsonPatchApplicationKey = jsonPatchFingerprint
        ? getIncrementalEditApplicationKey(
            currentSessionId,
            allAnswers.length - 1,
            jsonPatchFingerprint
          )
        : null
      const incompleteStaticTile = hasIncompleteStaticTileAddition(content)
      if (
        !spec &&
        currentDashboardSpecRef.current &&
        !wasIncrementalEditApplied(currentDashboardSpecRef.current, jsonPatchApplicationKey) &&
        shouldApplyIncrementalResponse(
          lastAppliedIncrementalFingerprintRef.current,
          jsonPatchFingerprint
        )
      ) {
        if (incompleteStaticTile) {
          lastAppliedIncrementalFingerprintRef.current = jsonPatchFingerprint
          toast.warning(
            t("dash.add_tile.empty_data", {
              defaultValue:
                "The assistant returned an empty tile. The dashboard was left unchanged; try attaching the source file again.",
            }),
            { id: ADD_TILE_TOAST_ID, duration: 14000 }
          )
        } else {
          spec = extractAndApplyJsonPatch(content, currentDashboardSpecRef.current, latestAnswer[0])
          appliedIncrementalEdit = spec !== null
          if (appliedIncrementalEdit) {
            appliedIncrementalFingerprint = jsonPatchFingerprint
            appliedIncrementalApplicationKey = jsonPatchApplicationKey
          }
        }
      }
      const yamlDiffFingerprint = getIncrementalDashboardEditFingerprint(content, "yaml-diff")
      const yamlDiffApplicationKey = yamlDiffFingerprint
        ? getIncrementalEditApplicationKey(
            currentSessionId,
            allAnswers.length - 1,
            yamlDiffFingerprint
          )
        : null
      if (
        !spec &&
        currentDashboardSpecRef.current &&
        !wasIncrementalEditApplied(currentDashboardSpecRef.current, yamlDiffApplicationKey) &&
        shouldApplyIncrementalResponse(
          lastAppliedIncrementalFingerprintRef.current,
          yamlDiffFingerprint
        )
      ) {
        spec = extractAndApplyYamlDiff(content, currentDashboardSpecRef.current)
        appliedIncrementalEdit = spec !== null
        if (appliedIncrementalEdit) {
          appliedIncrementalFingerprint = yamlDiffFingerprint
          appliedIncrementalApplicationKey = yamlDiffApplicationKey
        }
      }
      if (!spec && currentDashboardSpecRef.current) {
        spec = extractAndApplyTileRender(content, currentDashboardSpecRef.current, latestAnswer[0])
      }
    }

    if (spec) {
      if (appliedIncrementalEdit && appliedIncrementalFingerprint) {
        lastAppliedIncrementalFingerprintRef.current = appliedIncrementalFingerprint
      }
      if (appliedIncrementalEdit && appliedIncrementalApplicationKey) {
        spec = markIncrementalEditApplied(spec, appliedIncrementalApplicationKey)
      }
      const lastUserMsg = !chatState.isStreaming ? chatState.messages.at(-1)?.[0] : undefined
      const enriched = enrichDashboardSpecRef.current(spec, latestAnswer[1], lastUserMsg)
      // When the autosave pipeline has already saved + refreshed past
      // this content, the live store carries refreshed values plus a
      // hydrated queryBindings array. Re-extracting from the same
      // message would otherwise blow those away (badges flip from
      // Live SQL back to AI, data goes from real numbers back to
      // empty `[]`).
      //
      // We compare against `lastRefreshedContentRef.current` rather
      // than a JSON snapshot of the spec — `enrichDashboardSpec`
      // pulls in mutable signals (enabled libraries, document
      // selections) so two structurally-equivalent extractions yield
      // different JSON even when the underlying chat message hasn't
      // moved. Message content is a stable identity for "this is the
      // same yaml-render we already refreshed past".
      if (lastRefreshedContentRef.current != null && lastRefreshedContentRef.current === content) {
        const liveState = explorerStore.getState()
        if (!chatState.isStreaming && liveState.pendingSaveAfterRefresh) {
          liveState.setPendingSaveAfterRefresh(false)
        }
        return
      }
      setDashboardSpec(enriched)
      const liveState = explorerStore.getState()
      if (!chatState.isStreaming && liveState.pendingSaveAfterRefresh) {
        liveState.setPendingSaveAfterRefresh(false)
        saveRefreshedDashboardRef.current(enriched)
      }
      if (!dashboardPanelVisible) setDashboardPanelVisible(true)
    } else if (!chatState.isStreaming) {
      const liveState = explorerStore.getState()
      if (liveState.pendingSaveAfterRefresh) liveState.setPendingSaveAfterRefresh(false)
    }
  }, [
    chatState.isStreaming,
    chatState.messages,
    chatState.streamingAnswer,
    currentSessionId,
    dashboardMode,
    dashboardPanelVisible,
    setDashboardPanelVisible,
    setDashboardSpec,
    explorerStore,
    t,
  ])

  const setCurrentDashboardId = explorerStore((state) => state.setCurrentDashboardId)
  const setCurrentDashboardTitle = explorerStore((state) => state.setCurrentDashboardTitle)
  const setCurrentDashboardType = explorerStore((state) => state.setCurrentDashboardType)
  const setLastUpdated = explorerStore((state) => state.setLastUpdated)

  // === Auto-save on chat streaming completion ===
  // Manual "Spara" is redundant once the agent generates: create on first
  // streamed yaml-render, update on subsequent ones. Triggered on the
  // isStreaming true → false transition so we save exactly once per turn.
  //
  // Guards (defense against the SHL Klubbar overwrite pattern fixed in
  // a32ed4ba):
  //   - Only fires in `dashboardMode === "chat"`.
  //   - Skips when persisted dashboardSessionId ≠ current — a stale ID
  //     paired with a fresh spec means the user is in a new chat against
  //     someone else's dashboard; saving would overwrite their work.
  //   - Skips no-op writes (JSON-equal spec).
  const { data: dashboards = [], isLoading: dashboardsLoading } = useDashboards()
  const createDashboard = useCreateDashboard()
  const updateDashboard = useUpdateDashboard()
  const currentDashboardId = explorerStore((state) => state.currentDashboardId)
  const currentDashboardTitle = explorerStore((state) => state.currentDashboardTitle)
  const currentDashboardType = explorerStore((state) => state.currentDashboardType)
  const currentDashboard = currentDashboardId
    ? dashboards.find((dashboard) => dashboard.id === currentDashboardId)
    : undefined
  const isDashboardAdmin = SettingsService.getInstance().settings.user_policy.is_prompt_admin
  const hasDashboardEditPermission =
    !currentDashboardId ||
    (currentDashboardType === "personal" && currentDashboard?.currentUserRole !== "reader") ||
    isDashboardAdmin
  const restoredDashboardRouteRef = useRef<string | null>(null)
  // Returning to /dash clears several store fields while React Router may
  // still expose the previous /dash/chat/:sessionId for one render. Without
  // this intent guard, the route hydration effect can restore the dashboard
  // we are actively closing and leave its panel hidden over an empty chat.
  const returningToLandingRef = useRef(false)
  useEffect(() => {
    if (!urlSessionId || dashboardsLoading) return
    if (returningToLandingRef.current) return
    if (explorerStore.getState().pendingNewDashboard) return
    if (restoredDashboardRouteRef.current === urlSessionId) return
    if (dashboardMode !== "landing" || currentDashboardId) return

    const dashboard = findDashboardBySessionId(dashboards, urlSessionId)
    if (!dashboard) return

    restoredDashboardRouteRef.current = urlSessionId
    ignoreHydratedDashboardHistoryRef.current = true
    loadDashboard(
      dashboard.id,
      {
        ...(dashboard.spec as DashboardSpec),
        queryBindings: dashboard.query_bindings ?? (dashboard.spec as DashboardSpec).queryBindings,
      },
      {
        updateOnLoad: dashboard.update_on_load,
        dashboardType: dashboard.type,
        title: dashboard.title,
        qualityReview: dashboard.quality_review,
        qualityHistory: dashboard.quality_history,
      }
    )
    setLastUpdated(new Date(dashboard.updated || dashboard.created))
    trackRecentlyViewed(dashboard.id)
  }, [
    currentDashboardId,
    dashboardMode,
    dashboards,
    dashboardsLoading,
    loadDashboard,
    setLastUpdated,
    urlSessionId,
  ])
  useEffect(() => {
    if (!urlSessionId && dashboardMode === "landing") {
      returningToLandingRef.current = false
    }
  }, [dashboardMode, urlSessionId])
  const prevIsStreamingRef = useRef(false)
  // Keyed on (id, json) so opening a different dashboard never gets
  // skipped as a no-op just because its starting spec happens to JSON-
  // equal a previously-saved one.
  saveRefreshedDashboardRef.current = (spec) => {
    const liveState = explorerStore.getState()
    const dashboardId = liveState.currentDashboardId
    const sessionId = liveState.currentSessionId
    if (!dashboardId) return
    const specSessionId = spec.dashboardSessionId?.trim()
    if (specSessionId && sessionId && specSessionId !== sessionId) {
      logger.warn("AI refresh save skipped: dashboardSessionId mismatch", {
        dashboardId,
        specSessionId,
        sessionId,
      })
      return
    }

    void updateDashboard
      .mutateAsync({
        dashboardId,
        payload: {
          spec: spec as Record<string, unknown>,
          type: liveState.currentDashboardType,
        },
      })
      .then(() => {
        const current = explorerStore.getState()
        if (current.currentDashboardId !== dashboardId || current.currentSessionId !== sessionId) {
          return
        }
        toast.success(t("dash.dashboard.auto_saved"))
      })
      .catch((error) => {
        toast.error(t("dash.dashboard.save_error"))
        logger.error("Auto-save after AI refresh failed", error as Error)
      })
  }

  const lastSavedRef = useRef<{ id: string | null; json: string } | null>(null)
  const autoSavePendingGenerationRef = useRef<number | null>(null)
  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current
    prevIsStreamingRef.current = chatState.isStreaming

    if (!wasStreaming || chatState.isStreaming) return
    if (dashboardMode !== "chat") return
    const live = explorerStore.getState()
    const documentGenerationAtFire = live.dashboardDocumentGeneration
    if (autoSavePendingGenerationRef.current === documentGenerationAtFire) return

    // Read live store state — the spec-extract effect runs before this
    // one (declared earlier in the file) and may have synchronously
    // setDashboardSpec'd a post-streaming yaml-edit / yaml-diff merge
    // in the same render cycle. Selector closures here still hold the
    // pre-extract value; reading via getState() returns the latest
    // committed spec. Observed 2026-05-12: a follow-up turn's surgical
    // patch added a KPI tile visually but the PUT shipped the prior
    // spec, losing the tile on reload.
    const specForSave = live.currentDashboardSpec
    const dashboardIdAtFire = live.currentDashboardId
    if (!specForSave) return

    const specJson = JSON.stringify(specForSave)
    if (
      lastSavedRef.current &&
      lastSavedRef.current.id === dashboardIdAtFire &&
      lastSavedRef.current.json === specJson
    ) {
      return
    }
    if (dashboardIdAtFire) {
      const persisted = dashboards.find((d) => d.id === dashboardIdAtFire)
      const persistedSession = (persisted?.spec as { dashboardSessionId?: string } | undefined)
        ?.dashboardSessionId
      const currentSession = (specForSave as { dashboardSessionId?: string }).dashboardSessionId
      if (persistedSession && currentSession && persistedSession !== currentSession) {
        logger.warn("Auto-save skipped: dashboardSessionId mismatch", {
          dashboardId: dashboardIdAtFire,
          persistedSession,
          currentSession,
        })
        return
      }
    }

    const specTitle = (specForSave as { title?: unknown }).title
    const resolvedTitle =
      (typeof currentDashboardTitle === "string" && currentDashboardTitle.trim()) ||
      (typeof specTitle === "string" && specTitle.trim()) ||
      t("dash.dashboard.untitled")

    autoSavePendingGenerationRef.current = documentGenerationAtFire
    void (async () => {
      try {
        let savedId: string | null = null
        let savedDashboard: SavedDashboard | null = null
        if (dashboardIdAtFire) {
          savedDashboard = await updateDashboard.mutateAsync({
            dashboardId: dashboardIdAtFire,
            payload: {
              title: resolvedTitle,
              spec: specForSave as unknown as Record<string, unknown>,
              type: currentDashboardType,
            },
          })
          savedId = dashboardIdAtFire
        } else {
          const uniqueTitle = getUniqueDashboardTitle(
            resolvedTitle,
            dashboards.map((dashboard) => dashboard.title)
          )
          savedDashboard = await createDashboard.mutateAsync({
            title: uniqueTitle,
            description: "",
            spec: specForSave as unknown as Record<string, unknown>,
            type: currentDashboardType,
            // Chat-generated dashboards should auto-refresh on every
            // open: the user just described what they want, they
            // expect live data, not the `data: []` placeholders the
            // agent emits alongside `_sql` hints. Subsequent reloads
            // (F5, sidebar navigation) hit `loadDashboard` which
            // forwards this flag into `pendingAutoRefresh`.
            update_on_load: true,
          })
          if (explorerStore.getState().dashboardDocumentGeneration === documentGenerationAtFire) {
            setCurrentDashboardId(savedDashboard.id)
            if (!currentDashboardTitle) {
              setCurrentDashboardTitle(uniqueTitle)
            }
          }
          savedId = savedDashboard.id
        }
        if (explorerStore.getState().dashboardDocumentGeneration !== documentGenerationAtFire) {
          return
        }
        lastSavedRef.current = { id: savedId, json: specJson }
        if (savedId) trackRecentlyViewed(savedId)

        // Auto-execute the freshly-extracted bindings so the user sees
        // real data immediately instead of "Inga data" placeholders.
        // The backend lifted inline `_sql` hints into `query_bindings`
        // during save; this call runs them. Without it, the streamed
        // spec keeps its `data: []` / `value: 0` shells until the user
        // manually clicks "Uppdatera data" — the regression the user
        // hit as "first AI tiles, save + refresh + luck = SQL tiles".
        if (savedId && (savedDashboard?.query_bindings?.length ?? 0) > 0) {
          try {
            const stateSnapshot = dashboardStateGetterRef.current?.() ?? undefined
            const refreshed = await DashboardService.refreshDashboard(savedId, stateSnapshot)
            const live = explorerStore.getState()
            // Skip the commit when the user has streamed a newer spec
            // in the meantime (next chat turn already started). The
            // `(id, json)` match guarantees we're still pointed at the
            // same dashboard the autosave just persisted.
            const stillSameSpec =
              refreshed != null &&
              live.currentDashboardId === savedId &&
              live.currentDashboardSpec != null &&
              JSON.stringify(live.currentDashboardSpec) === specJson
            if (stillSameSpec) {
              setDashboardSpec({
                ...(refreshed.spec as DashboardSpec),
                queryBindings: (savedDashboard?.query_bindings ??
                  []) as DashboardSpec["queryBindings"],
              })
              // Mark the chat-message content this refresh committed
              // for so the extract effect can skip later re-fires for
              // the same message (post-stream metadata updates from
              // the backend, no new yaml-render in the message). The
              // message reference is more stable than the enriched
              // spec JSON because enrichDashboardSpec pulls in
              // mutable signals (enabled libraries, document
              // selections) that drift independently of the actual
              // content.
              const latestStore = explorerStore.getState()
              const sessionId = latestStore.currentSessionId
              const sessionState = sessionId ? latestStore.sessionStates[sessionId] : undefined
              const allMessages = sessionState?.streamingAnswer
                ? [...(sessionState.messages ?? []), sessionState.streamingAnswer]
                : (sessionState?.messages ?? [])
              const lastResponse = allMessages.at(-1)?.[1]
              // eslint-disable-next-line max-depth
              if (lastResponse) {
                lastRefreshedContentRef.current = getResponseContent(lastResponse)
              }
            }
          } catch (err) {
            logger.warn("Auto-refresh after autosave failed", err as Error)
          }
        }
      } catch (err) {
        logger.warn("Auto-save dashboard failed", err as Error)
      } finally {
        if (autoSavePendingGenerationRef.current === documentGenerationAtFire) {
          autoSavePendingGenerationRef.current = null
        }
      }
    })()
  }, [
    chatState.isStreaming,
    dashboardMode,
    currentDashboardSpec,
    currentDashboardId,
    currentDashboardTitle,
    currentDashboardType,
    dashboards,
    createDashboard,
    updateDashboard,
    setCurrentDashboardId,
    setCurrentDashboardTitle,
    setDashboardSpec,
    explorerStore,
    t,
  ])

  // The sidebar marks its landing transition before clearing the active
  // dashboard. Keep the flag until the chat route has disappeared so the
  // route-hydration effect cannot reopen the dashboard being closed.
  const pendingNewDashboard = explorerStore((state) => state.pendingNewDashboard)
  const handledPendingNewDashboardRef = useRef(false)
  useEffect(() => {
    if (!pendingNewDashboard) {
      handledPendingNewDashboardRef.current = false
      return
    }
    if (!handledPendingNewDashboardRef.current) {
      handledPendingNewDashboardRef.current = true
      handleResetFiles()
    }
    if (urlSessionId || dashboardMode !== "landing") return
    explorerStore.setState({ pendingNewDashboard: false })
  }, [dashboardMode, explorerStore, handleResetFiles, pendingNewDashboard, urlSessionId])

  // Pending question from the landing page chatbox.
  // Stored in state so a useEffect can send it after React commits the mode
  // switch, ensuring handleSendQuestion uses fresh closures (not stale chat state
  // from a previous session).
  const [pendingLandingQuestion, setPendingLandingQuestion] = useState<{
    question: string
    files: File[]
  } | null>(null)

  const handleCreateNewDashboard = useCallback(
    (question: string) => {
      // Snapshot any attached files BEFORE handleStartNewChat() runs: that
      // helper calls resetFiles(), which clears explorerStore.files. Without
      // capturing here, the deferred send below would read an empty list and
      // the uploaded file would never reach the agent (it falls back to
      // "please upload the file first"). The send forwards these via the
      // explicit `files` override → effectiveFiles → the WS base64 payload.
      const attachedFiles = [...files]
      explorerStore.getState().beginNewDashboardDocument()
      ignoreHydratedDashboardHistoryRef.current = false
      setDashboardMode("chat")
      setDashboardSpec(null)
      setCurrentDashboardId(null)
      setLastUpdated(null)
      handleStartNewChat()
      setPendingLandingQuestion({ question, files: attachedFiles })
    },
    [
      files,
      explorerStore,
      setDashboardMode,
      setDashboardSpec,
      setCurrentDashboardId,
      setLastUpdated,
      handleStartNewChat,
    ]
  )

  // Send the pending landing question once the chat mode is active.
  // Using a ref ensures we always call the latest handleSendQuestion without
  // triggering the effect on every render (the function is not memoized).
  const handleSendQuestionRef = useRef(sendDashboardQuestion)
  handleSendQuestionRef.current = sendDashboardQuestion

  useEffect(() => {
    if (!pendingLandingQuestion || dashboardMode !== "chat" || isM365ApprovalLoading) return
    const { question, files: pendingFiles } = pendingLandingQuestion
    setPendingLandingQuestion(null)
    void handleSendQuestionRef.current({
      question,
      ...(pendingFiles.length > 0 ? { files: pendingFiles } : {}),
    })
  }, [pendingLandingQuestion, dashboardMode, isM365ApprovalLoading])

  const handleOpenDashboard = useCallback(
    async (dashboard: SavedDashboard) => {
      trackRecentlyViewed(dashboard.id)
      const specWithSession = await attachDashboardSessionId(dashboard)
      restoredDashboardRouteRef.current = specWithSession.dashboardSessionId ?? null
      ignoreHydratedDashboardHistoryRef.current = true
      loadDashboard(dashboard.id, specWithSession, {
        updateOnLoad: dashboard.update_on_load,
        dashboardType: dashboard.type,
        title: dashboard.title,
      })
      setLastUpdated(new Date(dashboard.updated || dashboard.created))
    },
    [attachDashboardSessionId, loadDashboard, setLastUpdated]
  )

  const handleEditDashboard = useCallback(
    async (dashboard: SavedDashboard) => {
      trackRecentlyViewed(dashboard.id)
      const specWithSession = await attachDashboardSessionId(dashboard)
      restoredDashboardRouteRef.current = specWithSession.dashboardSessionId ?? null
      ignoreHydratedDashboardHistoryRef.current = true
      loadDashboard(dashboard.id, specWithSession, {
        updateOnLoad: dashboard.update_on_load,
        dashboardType: dashboard.type,
        title: dashboard.title,
      })
      setLastUpdated(new Date(dashboard.updated || dashboard.created))
      // loadDashboard sets mode="view", override to "chat" for edit mode
      setDashboardMode("chat")
    },
    [attachDashboardSessionId, loadDashboard, setDashboardMode, setLastUpdated]
  )

  const handleBackToLanding = useCallback(() => {
    returningToLandingRef.current = true
    setDashboardPanelVisible(false)
    const contentSidebar = useContentSidebarStore.getState()
    if (
      contentSidebar.currentPanel?.id === "dashboard" ||
      contentSidebar.currentPanel?.id === DASH_CONTEXT_PANEL_ID
    ) {
      contentSidebar.forceClosePanel()
    }
    setDashboardMode("landing")
    setDashboardSpec(null)
    setSelectedTileIndex(null)
    setCurrentDashboardId(null)
    setCurrentDashboardTitle("")
    setCurrentDashboardType("personal")
    setLastUpdated(null)
    void navigate("/dash", { replace: true })
  }, [
    setDashboardPanelVisible,
    setDashboardMode,
    setDashboardSpec,
    setSelectedTileIndex,
    setCurrentDashboardId,
    setCurrentDashboardTitle,
    setCurrentDashboardType,
    setLastUpdated,
    navigate,
  ])

  // The Dash store intentionally survives route unmounts so chat sessions can
  // be restored from /dash/chat/:sessionId. A plain /dash module entry has no
  // such identity, however, and must never inherit the dashboard that happened
  // to be open before the user left for another module. Reconcile only once on
  // mount so the brief route-less state while creating a new dashboard is not
  // mistaken for a module entry. A layout effect prevents stale dashboard
  // chrome from flashing before the landing page is rendered.
  const didReconcileModuleEntryRef = useRef(false)
  useLayoutEffect(() => {
    if (didReconcileModuleEntryRef.current) return
    didReconcileModuleEntryRef.current = true
    if (urlSessionId) return
    if (explorerStore.getState().dashboardMode === "landing") return
    handleBackToLanding()
  }, [explorerStore, handleBackToLanding, urlSessionId])

  // A dashboard opened from /dash pushes its chat route onto history. When
  // the user returns with the browser Back button, mirror the route change in
  // the Dash store. Keying this to a POP location avoids interfering with the
  // brief route-less state while a brand-new dashboard session is created.
  useEffect(() => {
    if (navigationType !== "POP" || urlSessionId) return
    if (explorerStore.getState().dashboardMode === "landing") return
    handleBackToLanding()
  }, [explorerStore, handleBackToLanding, location.key, navigationType, urlSessionId])

  const setPendingSaveAfterRefresh = explorerStore((state) => state.setPendingSaveAfterRefresh)

  // Getter registered by DashboardStateProvider via onStateReady — lets us
  // snapshot the live dashboard state (filters, selections) at refresh time
  // so bindings using params_from_state get substituted with real values
  // instead of NULL.
  const dashboardStateGetterRef = useRef<(() => Record<string, unknown>) | null>(null)
  const handleDashboardStateReady = useCallback((getState: () => Record<string, unknown>) => {
    dashboardStateGetterRef.current = getState
  }, [])

  // #2967: the agent never rewrites a dashboard without the user saying yes.
  // `handleRefreshData` awaits this before its AI fallback; the dialog below
  // resolves it.
  const [aiRefreshConfirm, setAiRefreshConfirm] = useState<{
    resolve: (confirmed: boolean) => void
  } | null>(null)
  const confirmAiRefresh = useCallback(
    () => new Promise<boolean>((resolve) => setAiRefreshConfirm({ resolve })),
    []
  )

  const handleRefreshData = useCallback(
    async (options?: { allowAiRefresh?: boolean }) => {
      const storeState = explorerStore.getState()
      if (storeState.isDashboardRefreshing) return
      const spec = storeState.currentDashboardSpec
      if (!spec) return
      const dashboardId = storeState.currentDashboardId
      const savedContext = spec.sourceContext
      const dashboardState = dashboardStateGetterRef.current?.() ?? undefined

      // Try SQL refresh first if dashboard has query bindings
      if (dashboardId && spec.queryBindings?.length) {
        setDashboardRefreshing(true)
        try {
          const result = await DashboardService.refreshDashboard(dashboardId, dashboardState)
          if (result) {
            // Backend strips queryBindings from the stored spec; preserve the
            // runtime merge so tile badges stay "Live SQL" after refresh.
            const refreshedSpec: DashboardSpec = {
              ...(result.spec as DashboardSpec),
              queryBindings: spec.queryBindings,
            }
            setDashboardSpec(refreshedSpec)
            if (result.refresh_results.failed > 0) {
              toast.warning(
                t("dash.dashboard.refresh_partial_failure", {
                  defaultValue: "{{count}} data source(s) failed to refresh",
                  count: result.refresh_results.failed,
                })
              )
            } else {
              toast.success(
                t("dash.dashboard.refresh_success", {
                  defaultValue: "Dashboard data refreshed",
                })
              )
            }
            return
          }
          // result is null → no bindings on server side, fall through to AI refresh
        } catch (err) {
          logger.error("SQL refresh failed, falling back to AI refresh", err as Error)
        } finally {
          setDashboardRefreshing(false)
        }
      }

      // AI-driven refresh (fallback)
      if (storeState.currentDashboardId) {
        if (options?.allowAiRefresh === false) return
      }

      // A full agent run that rewrites (and, for a saved dashboard, auto-saves
      // over) the dashboard — confirmed first, whether the refresh came from
      // the toolbar or from an automatic trigger (#2967).
      if (!(await confirmAiRefresh())) return

      if (storeState.currentDashboardId) {
        setPendingSaveAfterRefresh(true)
      }

      setDashboardRefreshing(true)
      try {
        if (savedContext?.sourceUrls) {
          setSourceUrls(savedContext.sourceUrls.join("\n"))
        }
        if (savedContext?.trustedMode) {
          setTrustedMode(savedContext.trustedMode)
        }
        if (savedContext?.searchServices) {
          const desiredEnabled = new Set(savedContext.searchServices)
          Object.keys(enabledLibraries).forEach((libraryId) => {
            const currentlyEnabled = enabledLibraries[libraryId]?.checked ?? false
            const shouldEnable = desiredEnabled.has(libraryId)
            if (currentlyEnabled !== shouldEnable) {
              toggleLibraryChecked(libraryId)
            }
          })
        }

        const specJson = JSON.stringify(spec, null, 2)
        const refreshPrompt = [
          t("dash.dashboard.refresh_prompt", {
            defaultValue:
              "Refresh the data in this dashboard. Keep the exact same structure, titles, and visualization types. Fetch fresh data from the same sources.",
          }),
          "",
          t("dash.dashboard.current_spec_label", { defaultValue: "Current dashboard spec:" }),
          "```json",
          specJson,
          "```",
        ].join("\n")

        await sendDashboardQuestion({ question: refreshPrompt })
      } finally {
        setDashboardRefreshing(false)
      }
    },
    [
      confirmAiRefresh,
      enabledLibraries,
      explorerStore,
      sendDashboardQuestion,
      setDashboardRefreshing,
      setDashboardSpec,
      setPendingSaveAfterRefresh,
      setSourceUrls,
      setTrustedMode,
      t,
      toggleLibraryChecked,
    ]
  )

  // Stable ref so DashboardSidebarContent always calls the latest version
  const handleRefreshDataRef = useRef(handleRefreshData)
  handleRefreshDataRef.current = handleRefreshData
  const stableRefreshData = useCallback(async (options?: { allowAiRefresh?: boolean }) => {
    await handleRefreshDataRef.current(options)
  }, [])

  // Handler for "Edit with AI" button in the tile editor.
  //
  // Two modes:
  //   - description omitted → legacy behavior, just hint the composer
  //     ("Edit panel N: title"). User finishes typing themselves.
  //   - description provided → Feature #3: build a yaml-patch prompt
  //     with the tile's current spec + the user's change request and
  //     auto-submit. Agent emits an RFC 6902 `replace` patch surgically
  //     mutating the named tile and nothing else.
  const handleEditTileWithAI = useCallback(
    (tileIndex: number, tileTitle: string, description?: string) => {
      const label = tileTitle || `panel ${tileIndex + 1}`
      const spec = currentDashboardSpec
      if (description && spec) {
        // Build element path from flat index. The popover indexes via
        // getFlatTiles which traverses depth-first; for the prompt we
        // need a JSON Pointer compatible with yaml-patch. Map back to
        // the original spec via resolveTileByFlatIndex if available;
        // otherwise fall back to children[N].
        const elementPath = resolveTilePathByFlatIndex(spec, tileIndex)
        const tile = elementPath ? resolveTileByPath(spec, elementPath) : spec.children?.[tileIndex]
        if (elementPath && tile) {
          const prompt = buildEditTilePrompt({
            description,
            elementPath,
            tile: tile as DashboardElement,
            // Surface the bound SQL + last_error + data sample so the
            // agent can answer "why doesn't this work" type questions.
            // Without queryBindings the prompt only describes structure
            // and the agent has nothing to investigate.
            queryBindings: spec.queryBindings,
          })
          if (prompt) {
            void handleSendQuestionRef.current({ question: prompt })
            return
          }
        }
      }
      // Legacy fallback: hint the composer so the user can finish typing.
      const prompt = t("dash.tile_edit.ai_prompt", {
        defaultValue: "Edit panel {{index}}: {{label}}",
        index: tileIndex + 1,
        label,
      })
      if (setQuestionFunction) {
        setQuestionFunction(prompt)
      }
    },
    [currentDashboardSpec, setQuestionFunction, t]
  )
  const handleEditTileWithAIRef = useRef(handleEditTileWithAI)
  handleEditTileWithAIRef.current = handleEditTileWithAI
  const stableEditTileWithAI = useCallback(
    (tileIndex: number, tileTitle: string, description?: string) => {
      handleEditTileWithAIRef.current(tileIndex, tileTitle, description)
    },
    []
  )

  // "+ Add tile" slot — user describes the tile in natural language;
  // we hand the description + insertion index to buildAddTilePrompt
  // (utils/add-tile-prompt.ts), and auto-submit via handleSendQuestion
  // so the agent receives it immediately. The agent emits a yaml-patch
  // fence that splices one tile into spec.children at the requested
  // index (already routed via spec-parser at dash-page.tsx:1092).
  //
  // Why auto-submit (not just setQuestionFunction): the slot's UX
  // is "describe and click Generate". Filling the composer and
  // requiring a second click on Send makes the flow feel broken —
  // the user already pressed Generate.
  // add-tile is fire-and-forget — the slot dialog closes immediately on
  // submit and the agent's reply streams into chat. These refs let the
  // completion effect below recognise an add-tile turn so it can give
  // the user feedback: the agent sometimes declines (e.g. data it can't
  // reach) and that used to be completely silent.
  const addTilePendingRef = useRef<{ beforeSpecJson: string } | null>(null)
  const prevStreamingAddTileRef = useRef(false)

  const handleAddTile = useCallback(
    (description: string, insertionIndex: number) => {
      const prompt = buildAddTilePrompt({
        description,
        insertionIndex,
        childrenCount: currentDashboardSpec?.children.length ?? 0,
      })
      if (!prompt) return
      addTilePendingRef.current = { beforeSpecJson: JSON.stringify(currentDashboardSpec) }
      toast.loading(t("dash.add_tile.working", { defaultValue: "Generating tile…" }), {
        id: ADD_TILE_TOAST_ID,
      })
      void handleSendQuestionRef.current({ question: prompt }).catch(() => {
        // The request failed before a stream ever started, so the
        // streaming true→false effect below will never fire. Clear the
        // pending flag here so a later turn doesn't inherit it, and
        // replace the stuck loading toast.
        addTilePendingRef.current = null
        toast.error(
          t("dash.add_tile.no_result", {
            defaultValue: "The assistant couldn't add that tile.",
          }),
          { id: ADD_TILE_TOAST_ID }
        )
      })
    },
    [currentDashboardSpec, t]
  )
  const handleAddTileRef = useRef(handleAddTile)
  handleAddTileRef.current = handleAddTile
  const stableAddTile = useCallback((description: string, insertionIndex: number) => {
    handleAddTileRef.current(description, insertionIndex)
  }, [])

  // Add-tile completion feedback. Fires on the streaming true→false
  // transition for an add-tile turn: a `yaml-patch` fence in the reply
  // means a tile was generated; its absence means the agent answered in
  // prose (declined or asked a clarifying question) — surface that
  // reply so the click is never silent.
  useEffect(() => {
    const wasStreaming = prevStreamingAddTileRef.current
    prevStreamingAddTileRef.current = chatState.isStreaming
    if (!wasStreaming || chatState.isStreaming) return
    const pending = addTilePendingRef.current
    if (!pending) return
    addTilePendingRef.current = null
    // Read the just-finished answer the same way the spec-extract
    // effect does: a successful turn's answer lands in `messages`, an
    // errored one stays in `streamingAnswer`. Consulting both avoids
    // mis-reading a previous, unrelated turn on the error path.
    const latestAnswer = chatState.streamingAnswer
      ? chatState.streamingAnswer[1]
      : chatState.messages.at(-1)?.[1]
    const content = latestAnswer ? getResponseContent(latestAnswer).trim() : ""
    const latestSpec = explorerStore.getState().currentDashboardSpec
    const specChanged = latestSpec !== null && JSON.stringify(latestSpec) !== pending.beforeSpecJson
    if (hasIncompleteStaticTileAddition(content)) {
      toast.info(
        t("dash.add_tile.empty_data", {
          defaultValue:
            "The assistant returned an empty tile. The dashboard was left unchanged; try attaching the source file again.",
        }),
        { id: ADD_TILE_TOAST_ID, duration: 14000 }
      )
    } else if (specChanged || content.includes("```yaml-patch")) {
      toast.success(t("dash.add_tile.added", { defaultValue: "Tile added" }), {
        id: ADD_TILE_TOAST_ID,
      })
    } else {
      toast.info(
        content.slice(0, 600) ||
          t("dash.add_tile.no_result", {
            defaultValue: "The assistant couldn't add that tile.",
          }),
        { id: ADD_TILE_TOAST_ID, duration: 14000 }
      )
    }
  }, [chatState.isStreaming, chatState.messages, chatState.streamingAnswer, explorerStore, t])

  // "Update on load" effect: auto-refresh when opening a saved dashboard
  const pendingAutoRefresh = explorerStore((state) => state.pendingAutoRefresh)
  const clearPendingAutoRefresh = explorerStore((state) => state.clearPendingAutoRefresh)
  useEffect(() => {
    if (pendingAutoRefresh && dashboardMode === "view") {
      clearPendingAutoRefresh()
      void handleRefreshData({ allowAiRefresh: hasDashboardEditPermission })
    }
  }, [
    pendingAutoRefresh,
    dashboardMode,
    clearPendingAutoRefresh,
    handleRefreshData,
    hasDashboardEditPermission,
  ])

  // ─── Sync dashboardPanelVisible → ContentSidebar ────────────────
  useEffect(() => {
    const shouldBeOpen =
      dashboardPanelVisible && dashboardMode !== "landing" && currentDashboardSpec !== null
    const isDashboardOpen = useContentSidebarStore.getState().currentPanel?.id === "dashboard"

    if (shouldBeOpen) {
      if (!isDashboardOpen) {
        openContentPanel({
          id: "dashboard",
          headerMode: "none",
          content: (
            <DashboardSidebarContent
              sessionType={sessionType}
              onAddTile={stableAddTile}
              onBackToList={handleBackToLanding}
              onEditWithAI={stableEditTileWithAI}
              onRefresh={stableRefreshData}
              onStateReady={handleDashboardStateReady}
              onTogglePanel={() => setDashboardPanelVisible(false)}
            />
          ),
          focusable: true,
          closeable: true,
          resizable: true,
          onClose: () => setDashboardPanelVisible(false),
        })
      }

      // The panel can survive a client-side route transition while its focus
      // state does not. Re-focus in view mode even when it was already open,
      // otherwise a restored /dash/chat route leaves it at zero width.
      if (dashboardMode === "view") {
        setTimeout(() => {
          const s = useContentSidebarStore.getState()
          if (s.currentPanel?.focusable && !s.isFocused) s.toggleFocus()
        }, 0)
      }

      return
    }

    if (isDashboardOpen) {
      forceCloseContentPanel()
    }
  }, [dashboardPanelVisible, dashboardMode, currentDashboardSpec])

  // Landing mode has no chat/dashboard chrome — a lingering context panel
  // would float over the landing UI, so close it and drop any pending
  // dashboard-restore intent when entering landing.
  useEffect(() => {
    if (dashboardMode !== "landing") return
    dashboardVisibleBeforeContextRef.current = false
    if (useContentSidebarStore.getState().currentPanel?.id === DASH_CONTEXT_PANEL_ID) {
      forceCloseContentPanel()
    }
  }, [dashboardMode, forceCloseContentPanel])

  // Cleanup on unmount (navigating away from dash)
  useEffect(() => {
    return () => {
      const state = useContentSidebarStore.getState()
      if (
        state.currentPanel?.id === "dashboard" ||
        state.currentPanel?.id === DASH_CONTEXT_PANEL_ID
      ) {
        state.forceClosePanel()
      }
    }
  }, [])

  const showStartResearchButton =
    hasAgentModeFeature &&
    enableAgent &&
    hasLatestPlan &&
    !chatState.isLoading &&
    !chatState.isStreaming

  // Apply project defaults when project changes
  useProjectDefaults({
    activeProject,
    allowWebSearch: showWebSearchOption,
    enableWebSearch,
    handleWebSearchChange,
    availableModels: availableGptModels,
    currentGptModel: dbGptModel,
    modelSettingsHydrated,
    handleGptModelChange,
    sourceUrls: sourceUrlsInput,
    setSourceUrls,
    trustedMode,
    setTrustedMode,
  })

  // Show landing page when in landing mode
  if (dashboardMode === "landing") {
    return (
      <SidebarInset className="relative h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] overflow-hidden md:peer-data-[variant=inset]:mr-0">
        <DashboardLanding
          disableSend={isM365ApprovalLoading}
          files={files}
          modelControl={
            <ReasoningModePill
              availableModels={reasoningCapableModels}
              disabled={chatState.isLoading || chatState.isStreaming || isM365ApprovalLoading}
              effortOverride={effortOverride}
              mode={reasoningMode}
              modelOverride={modelOverride}
              selectedModel={selectedReasoningModel}
              onEffortOverrideChange={setEffortOverride}
              onModelOverrideChange={setModelOverride}
            />
          }
          onAddFiles={handleAddFiles}
          onEditDashboard={handleEditDashboard}
          onOpenDashboard={handleOpenDashboard}
          onRemoveFile={handleRemoveFile}
          onSendQuestion={handleCreateNewDashboard}
        />
      </SidebarInset>
    )
  }

  return (
    <Chat>
      <SidebarInset className="relative h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] overflow-hidden md:peer-data-[variant=inset]:mr-0">
        <div className="flex h-full min-h-0 flex-col">
          <Chat.Header className="border-border bg-card justify-between border-b px-4 py-2 lg:justify-end">
            <div className="mr-auto flex items-center gap-2">
              <div className="lg:hidden">
                <StartNewChat onStartNewChatClick={handleStartNewChat} />
              </div>
              {!dashboardPanelVisible && (
                <DashboardBreadcrumb
                  dashboardTitle={
                    currentDashboardTitle ||
                    currentDashboardSpec?.title ||
                    t("dash.dashboard.untitled")
                  }
                  onBackToList={handleBackToLanding}
                />
              )}
              {isReader && (
                <Badge
                  className="border-foreground bg-accent text-accent-foreground h-7 border px-3"
                  variant="outline"
                >
                  <Eye className="mr-1.5 size-3" />
                  {t("chatsession.reader_badge", { defaultValue: "READER" })}
                </Badge>
              )}
              {currentSessionId && (
                <Button
                  className="bg-background/85 hover:bg-background h-7 gap-1.5 px-3"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowShareDialog(true)}
                >
                  <Share2 className="size-3" />
                  {t("sharing.share", { defaultValue: "Share" })}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                aria-label={t("dash.dashboard.toggle_dashboard")}
                className="size-8"
                size="icon"
                title={t("dash.dashboard.toggle_dashboard")}
                variant="ghost"
                onClick={() => setDashboardPanelVisible(!dashboardPanelVisible)}
              >
                {dashboardPanelVisible ? (
                  <PanelRightClose className="size-4" />
                ) : (
                  <PanelRightOpen className="size-4" />
                )}
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={contextSidebarTriggerLabel}
                    aria-pressed={isContextPanelOpen}
                    data-testid="dash-context-sidebar-trigger"
                    size="icon"
                    type="button"
                    variant="ghost"
                    className={cn(
                      buttonVariants({
                        size: "icon",
                        variant: "icon",
                        className: "bg-background",
                      }),
                      "relative size-9 rounded-full",
                      isContextPanelOpen &&
                        "border-foreground bg-accent text-accent-foreground border"
                    )}
                    onClick={toggleContextPanel}
                  >
                    {isContextPanelOpen ? (
                      <PanelRightClose className="size-5" />
                    ) : (
                      <PanelRightOpen className="size-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{contextSidebarTriggerLabel}</TooltipContent>
              </Tooltip>
            </div>
          </Chat.Header>
          <TextSelectionProvider
            currentSessionId={currentSessionId}
            projectId={projectId}
            sessionType={sessionType}
            onSetQuestion={setQuestionFunction ?? undefined}
            onSendQuestion={async ({ question, startResearch, forceWebSearch }) =>
              sendDashboardQuestion({ question, startResearch, forceWebSearch })
            }
          >
            <Chat.Content>
              {answersForSpreadsheet.length === 0 &&
                !chatState.lastQuestion[0] &&
                !chatState.isLoading &&
                !chatState.streamingAnswer &&
                !aborted &&
                !chatState.error && (
                  <ChatHome
                    exampleQuestions={exampleQuestions}
                    onExampleQuestionClick={handleExampleClicked}
                  />
                )}
              {sharedHistoryMarkers.top.length > 0 && (
                <SharedHistoryMarkerLine
                  markers={sharedHistoryMarkers.top}
                  onManageSharing={handleManageSharing}
                />
              )}
              {/* Combine messages + streamingAnswer for display */}
              {(() => {
                const isStreamingAnswer = (index: number) =>
                  Boolean(chatState.streamingAnswer) && index === answersForSpreadsheet.length - 1

                return answersForSpreadsheet.map(
                  (answer: [string, ChatAppResponse], index: number) => {
                    // Skip invalid messages (missing response or invalid structure)
                    if (
                      !answer ||
                      !Array.isArray(answer) ||
                      answer.length < 2 ||
                      !answer[1] ||
                      typeof answer[1] !== "object"
                    ) {
                      return null
                    }
                    const answerKey = getMessageKey(answer[1], "answer", index)
                    try {
                      const isInternalQuestion = isInternalDashPrompt(answer[0])
                      const shouldShowUserMessage =
                        currentUserAccessMode !== "specific_message" &&
                        answer[0] &&
                        answer[0].trim() &&
                        !isInternalQuestion

                      // For specific_message mode, ensure activities are filtered out and error is handled
                      let filteredAnswer = answer[1]
                      if (currentUserAccessMode === "specific_message" && filteredAnswer) {
                        const needsFiltering =
                          "activities" in filteredAnswer ||
                          ("error" in filteredAnswer && !filteredAnswer.error) ||
                          ("error_id" in filteredAnswer && !filteredAnswer.error_id)

                        if (needsFiltering) {
                          filteredAnswer = { ...filteredAnswer }
                          delete filteredAnswer.activities
                          // Remove error/error_id if they are null/undefined to prevent "Unknown error"
                          if ("error" in filteredAnswer && !filteredAnswer.error) {
                            delete filteredAnswer.error
                          }
                          if ("error_id" in filteredAnswer && !filteredAnswer.error_id) {
                            delete filteredAnswer.error_id
                          }
                        }
                      }

                      const messageId =
                        filteredAnswer.messageId ||
                        getResponseContext(filteredAnswer).message_id ||
                        undefined
                      const beforeMarkers = messageId
                        ? sharedHistoryMarkers.before[messageId]
                        : undefined
                      const afterMarkers = messageId
                        ? sharedHistoryMarkers.after[messageId]
                        : undefined

                      return (
                        <section key={answerKey} className="flex flex-col gap-6">
                          {beforeMarkers?.length ? (
                            <SharedHistoryMarkerLine
                              markers={beforeMarkers}
                              onManageSharing={handleManageSharing}
                            />
                          ) : null}
                          {spreadSheetData.length > 0 && index === spreadSheetChatIndex && (
                            <SpreadsheetViewer data={spreadSheetData} />
                          )}
                          {shouldShowUserMessage && <Chat.UserMessage message={answer[0]} />}
                          <Chat.SystemMessage
                            showAnalysisPanelIcon
                            allowWebSearchAction={showWebSearchOption}
                            answer={filteredAnswer}
                            availableModels={availableGptModels}
                            citationSection={defaultSearchServiceId}
                            currentModelId={dbGptModel?.id}
                            index={index}
                            isLastMessage={index === answersForSpreadsheet.length - 1}
                            isReader={isReader}
                            isStreaming={isStreamingAnswer(index)}
                            messageId={filteredAnswer.messageId}
                            moduleName={moduleName}
                            noCitation={false}
                            sessionId={currentSessionId ?? undefined}
                            sessionType={sessionType}
                            userQuery={isInternalQuestion ? "" : answer[0]}
                            showFollowupQuestions={
                              suggestFollowupQuestions && answersForSpreadsheet.length - 1 === index
                            }
                            onBreakOutChat={handleBreakOutFromAIResponse}
                            onFollowupQuestionClicked={handleFollowupQuestionClicked}
                            onMessageUpdate={handleMessageUpdate}
                            onSendFollowUp={handleSendFollowUp}
                            onShareMessage={handleManageSharing}
                            onSwitchModel={handleSwitchModelAction}
                            onTryAgain={handleTryAgainAction}
                          />
                          {afterMarkers?.length ? (
                            <SharedHistoryMarkerLine
                              markers={afterMarkers}
                              onManageSharing={handleManageSharing}
                            />
                          ) : null}
                        </section>
                      )
                    } catch {
                      return null
                    }
                  }
                )
              })()}
              {sharedHistoryMarkers.bottom.length > 0 && (
                <SharedHistoryMarkerLine
                  markers={sharedHistoryMarkers.bottom}
                  onManageSharing={handleManageSharing}
                />
              )}
              {chatState.isLoading && !chatState.streamingAnswer && (
                <>
                  {currentUserAccessMode !== "specific_message" &&
                    chatState.lastQuestion[0] &&
                    !isInternalDashPrompt(chatState.lastQuestion[0]) && (
                      <Chat.UserMessage message={chatState.lastQuestion[0]} />
                    )}
                  <Chat.Loading />
                </>
              )}
              {chatState.error ? (
                <>
                  {!chatState.canContinue &&
                    currentUserAccessMode !== "specific_message" &&
                    chatState.lastQuestion[0] &&
                    !isInternalDashPrompt(chatState.lastQuestion[0]) && (
                      <Chat.UserMessage message={chatState.lastQuestion[0]} />
                    )}
                  <Chat.Error
                    canContinue={chatState.canContinue}
                    errorCode={getSessionErrorCode(chatState)}
                    error={(() => {
                      const message = (chatState.error as Error).toString()
                      return shouldLocalizeDashStreamError(message)
                        ? t("dash.chat.stream_error")
                        : message
                    })()}
                    onContinue={handleContinue}
                  />
                </>
              ) : null}
              {aborted ? (
                <>
                  {!chatState.canContinue &&
                    currentUserAccessMode !== "specific_message" &&
                    aborted[0] && <Chat.UserMessage message={aborted[0]} />}
                  <Chat.Error
                    canContinue={chatState.canContinue}
                    error={
                      t("pages.common.request_aborted") + "\n\n" + (aborted[1] ? aborted[1] : "")
                    }
                    onContinue={handleContinue}
                  />
                </>
              ) : null}
              <div ref={chatMessageStreamEnd} />
            </Chat.Content>
            <ToolApprovalOverlay
              activeSessionId={currentSessionId ?? null}
              approvals={chatState.toolApprovals ?? []}
              durableTurn={chatState.durableTurn ?? null}
              sidebarExpanded={isContentSidebarOpen}
              toolApprovalSessionId={chatState.toolApprovalSessionId ?? null}
            />
          </TextSelectionProvider>
          <Chat.Footer
            clearOnSend
            availableFiles={files}
            availableReasoningModels={reasoningCapableModels}
            cancelAvailable={!isCurrentSessionUncancellable}
            changeTokenCounts={changeTokenCounts}
            defaultFilePickerSource="local"
            dropzoneOptions={dropzoneOptions}
            effortOverride={effortOverride}
            enableHighReasoning={enableHighReasoning}
            filesInIndex={filesInIndex}
            hideVoiceInputButton={true}
            hideVoiceModeButton={false}
            isReader={isReader}
            maxFiles={MAX_FILES_UPLOAD}
            maxTokens={maxTokens}
            modelOverride={modelOverride}
            placeholder={t("pages.common.write_question_here")}
            projectId={projectId}
            reasoningMode={reasoningMode}
            removingFileName={removingFileName}
            resetFiles={handleResetFiles}
            selectedDocuments={selectedDocuments}
            selectedReasoningModel={selectedReasoningModel}
            sessionFileRefs={sessionFileRefs}
            sessionType={sessionType}
            showCancelOption={chatState.isLoading || chatState.isStreaming}
            showChatOptions={true}
            showHighReasoning={false}
            showReasoningMode={showReasoningMode}
            totalTokenCount={totalTokenCount}
            voiceSessionRoutePrefix={getRouteChatPath(sessionType)}
            actionButtons={
              showStartResearchButton ? (
                <button
                  className={cn(
                    buttonVariants({
                      size: "md",
                      variant: "default",
                    }),
                    "gap-2"
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    void handleStartResearch()
                  }}
                >
                  <Play className="size-4" />
                  {t("questioninput.start_research")}
                </button>
              ) : null
            }
            disableSend={
              isReader || chatState.isLoading || chatState.isStreaming || isM365ApprovalLoading
            }
            onBeforeVoiceModeStart={handleBeforeVoiceModeStart}
            onCancel={handleCancelRequest}
            onDocumentsSelected={handleDocumentsSelected}
            onEffortOverrideChange={setEffortOverride}
            onFilesAdded={handleAddFiles}
            onGetSendSessionContext={handleGetSendSessionContext}
            onGetToggleVoiceMode={handleGetToggleVoiceMode}
            onGraphFileSelected={handleGraphFileSelected}
            onHighReasoningChange={handleHighReasoningChange}
            onModelOverrideChange={setModelOverride}
            onReasoningModeChange={handleReasoningModeChange}
            onRemoveDocument={handleRemoveDocument}
            onSend={(question) => sendDashboardQuestion({ question })}
            onSetQuestion={handleSetQuestion}
            onStreamingComplete={handleStreamingComplete}
            onStreamingVoiceMessage={handleStreamingVoiceMessage}
            onVoiceMessage={handleVoiceMessage}
            onRemoveFile={(file) => {
              if (typeof file !== "string") {
                void handleRemoveFile(file)
                void handleRemoveIndexedFile(file.name)
                return
              }
              // String indicates an indexed filename
              void handleRemoveIndexedFile(file)
            }}
          >
            <ExplorerFooterToolbar
              enableAgent={enableAgent}
              enableHighReasoning={enableHighReasoning}
              enableM365={enableM365}
              enableNetbox={enableNetbox}
              enableServiceDesk={enableServiceDesk}
              enableWebSearch={enableWebSearch}
              hasAgentModeFeature={hasAgentModeFeature}
              isPromptsFeatureEnabled={isPromptsFeatureEnabled ?? false}
              isStreaming={chatState.isStreaming}
              sessionType={sessionType}
              showHighReasoning={showHighReasoning}
              showM365Option={showM365Option}
              showNetboxOption={showNetboxOption}
              showServiceDeskOption={showServiceDeskOption}
              showWebSearchOption={showWebSearchOption}
              onAgentChange={handleAgentChange}
              onHighReasoningChange={handleHighReasoningChange}
              onM365Change={handleM365Change}
              onNetboxChange={handleNetboxChange}
              onServiceDeskChange={handleServiceDeskChange}
              onStartVoiceMode={handleStartVoiceModeFromTools}
              onWebSearchChange={handleWebSearchChange}
              onPromptClicked={(prompt: PromptLibraryItem) => {
                // Set the prompt content in the question input
                if (setQuestionFunction) {
                  setQuestionFunction(prompt.content)
                }
                markNextMessageAsPromptTemplate()
                setSelectedPrompt(prompt.id, prompt.title)
                applyPromptSettings(prompt)
              }}
            />
          </Chat.Footer>
        </div>
      </SidebarInset>
      {projectId && (
        <ProjectsDialog
          open={showProjectsPanel}
          sessionType={sessionType}
          onOpenChange={setShowProjectsPanel}
        />
      )}
      <AgentModeInfoModal open={showAgentModeInfo} onClose={() => setShowAgentModeInfo(false)} />
      <M365InfoModal
        open={showM365Info}
        onApprove={handleM365Approve}
        onDecline={handleM365Decline}
        onDismiss={handleM365Dismiss}
      />
      <VoiceModeInfoModal
        engine={voiceEngine}
        open={showVoiceModeInfo}
        onAccept={handleVoiceModeAccept}
        onClose={() => setShowVoiceModeInfo(false)}
      />
      <ImageReasoningWarningModal
        open={showImageReasoningWarning}
        onCancel={handleImageReasoningWarningCancel}
        onClose={handleImageReasoningWarningClose}
      />
      <WorkflowInputDialog
        open={!!workflowToRun}
        workflow={workflowToRun}
        onOpenChange={(open) => !open && setWorkflowToRun(null)}
        onRun={handleWorkflowRun}
      />
      <AiRefreshConfirmDialog
        open={aiRefreshConfirm !== null}
        onDecide={(confirmed) => {
          aiRefreshConfirm?.resolve(confirmed)
          setAiRefreshConfirm(null)
        }}
      />

      {currentSessionId && (
        <ChatShareDialog
          chatStore={explorerStore}
          currentMessageId={shareMessageId ?? currentMessageId}
          open={showShareDialog}
          session={currentSession}
          sessionId={currentSessionId}
          onOpenChange={(open) => {
            setShowShareDialog(open)
            // Reset shareMessageId when dialog closes
            if (!open) {
              setShareMessageId(undefined)
            }
          }}
        />
      )}
    </Chat>
  )
}
